import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import {
  Users2, Plus, Trash2, X, Loader2, UserCog, GripVertical,
  Phone, Building2, ChevronDown, ChevronUp, MoreVertical, Pencil, History, Bell, RefreshCw, Paperclip, FileText, Download, Upload, ClipboardList, ArrowLeft,
  Table2, Link2, CheckCircle2, AlertTriangle, Copy, Unplug, ArrowRightLeft,
  PieChart, TrendingUp, Wallet, BarChart3, Eye, EyeOff,
} from "lucide-react";

// Input de CPF com botão de "olho" — fica mascarado por padrão (tipo
// senha) e o admin_company clica pra revelar/esconder na hora de digitar
// ou conferir, útil em telas compartilhadas/print.
function CpfInput({ value, onChange, style, small }) {
  const [visivel, setVisivel] = useState(false);
  return (
    <div style={{ position: "relative", ...style }}>
      <input placeholder="CPF" value={value} onChange={onChange} type={visivel ? "text" : "password"}
        style={small
          ? { width: "100%", padding: "5px 26px 5px 8px", borderRadius: 6, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 12, boxSizing: "border-box" }
          : { width: "100%", padding: "8px 32px 8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13, boxSizing: "border-box" }} />
      <button type="button" onClick={() => setVisivel(v => !v)} title={visivel ? "Esconder CPF" : "Mostrar CPF"}
        style={{ position: "absolute", right: small ? 4 : 6, top: "50%", transform: "translateY(-50%)", border: "none", background: "none", color: "hsl(var(--muted-foreground))", cursor: "pointer", padding: 2, display: "flex" }}>
        {visivel ? <EyeOff size={small ? 12 : 14} /> : <Eye size={small ? 12 : 14} />}
      </button>
    </div>
  );
}

/**
 * Pipeline de Empresa (BETA) — página nova e isolada, nada a ver com o
 * Pipeline do ProspectPage/MetaAdsPage. Cobre o ciclo comercial → backoffice
 * (Enviado BKO → Para Correção / Venda Perdida / Concluído), com visibilidade
 * por equipe: vendedor só vê os próprios cards; qualquer outro papel
 * (bko, coordenador, supervisor) vê o pipeline inteiro da operação.
 *
 * Só aparece no sidebar pra contas com is_company=true (liberado manualmente
 * pelo admin enquanto está em beta). Backend: /api/company/pipeline/*,
 * /api/team/members.
 */

const COLS = [
  { id: "lead",          label: "Lead",           color: "#3b82f6" },
  { id: "contato",       label: "Contato Feito",  color: "#f59e0b" },
  { id: "proposta",      label: "Proposta",       color: "#8b5cf6" },
  { id: "fechado",       label: "Fechado",        color: "#22c55e" },
  { id: "perdido",       label: "Perdido",        color: "#ef4444" },
  { id: "enviado_bko",   label: "Enviado BKO",    color: "#0ea5e9" },
  { id: "para_correcao", label: "Para Correção",  color: "#f97316" },
  { id: "perdido_bko",   label: "Cancelado",      color: "#dc2626" },
  { id: "concluido",     label: "Concluído",      color: "#16a34a" },
  { id: "instalado",     label: "Instalado",      color: "#0d9488" },
  { id: "ativo",         label: "Ativo",          color: "#65a30d" },
];
const COLS_COM_NOTA = new Set(["perdido", "para_correcao", "perdido_bko", "concluido"]);
const COLS_COM_DATA = new Set(["concluido", "instalado"]);

// Campos do checklist — mesma estrutura que já é reconhecida no "NOVO
// PEDIDO" via WhatsApp. Separado em duas listas só pra pintar visualmente
// diferente (preenchimento vendedor vs. backoffice) — qualquer um com
// acesso de edição no card pode preencher os dois grupos.
const CHECKLIST_CAMPOS_VENDEDOR = [
  ["nome_cliente", "Nome do Cliente"], ["cpf", "CPF"], ["rg", "RG"],
  ["data_nascimento", "Data de Nascimento"], ["nome_mae", "Nome da Mãe"],
  ["telefone_contato", "Telefone Contato"], ["telefone_whatsapp", "Telefone WhatsApp"],
  ["email", "Email"], ["cep", "CEP"], ["uf", "UF"], ["cidade", "Cidade"], ["bairro", "Bairro"],
  ["logradouro", "Logradouro"], ["numero_endereco", "Número"], ["complemento", "Complemento"],
  ["tipo_fatura", "Tipo da Fatura"],
  ["data_vencimento", "Data Vencimento"],
  ["plano_tv", "Plano de TV"], ["plano_internet", "Plano de Internet"],
  ["plano_fone", "Plano Fone"], ["plano_celular", "Plano Celular"],
  ["tipo_linha", "Tipo Linha"], ["tipo_linha_fixo", "Tipo Linha Fixo"],
];
const CHECKLIST_CAMPOS_BACKOFFICE = [
  ["numero_venda", "Número da Venda"],
  ["operador", "Operador"], ["indicacao", "Indicação"], ["venda_whatsapp", "Venda do WhatsApp"],
  ["telefone_auditoria", "Telefone Auditoria"], ["contrato", "Número Ordem"], ["proposta", "Proposta"],
  ["hp", "HP"], ["tipo_cliente", "Tipo de Cliente"], ["sistema_input", "Sistema Input"],
  ["status_rede", "Status Rede"], ["estrategia", "Portabilidade"], ["nome_campanha", "Nome Campanha"],
  ["nome_mailing", "Nome do Mailing"], ["marca", "Marca"], ["perfil_venda", "Perfil Venda"],
  ["login_z", "Login Z"], ["status_nivel_1", "Status Nível 1"], ["status_nivel_2", "Esteira de Agendamento"],
  ["status_nivel_3", "Valor da Venda"], ["data_venda", "Data da Venda"], ["data_gerada", "Data Gerada"],
  ["data_instalacao_checklist", "Data Instalação"], ["periodo_instalacao", "Período Instalação"],
  // Campos novos (pedido de ajuste da Sincronização de Base):
  ["uf_cliente", "UF do Cliente"], ["regional", "Regional"], ["plano", "Plano"],
  ["dia_vencimento", "Dia/Venc."], ["agendamento", "Agendamento"],
  ["dt_instalacao", "DT Instalação"], ["dt_cancelamento", "DT Cancelamento"],
  ["substatus_cancelado_pendente", "Sub Status (Cancelado/Pendente)"],
];

function fmtBRL(v) {
  return (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

// ═══════════════════ Gestão de equipe ═══════════════════
const NIVEL_PAPEL = { vendedor: 0, bko: 0, supervisor: 1, coordenador: 2, gerente: 3 };
const OPCOES_PAPEL = [
  { value: "vendedor", label: "Vendedor (só vê os próprios leads)" },
  { value: "bko", label: "Backoffice (vê a equipe abaixo)" },
  { value: "supervisor", label: "Supervisor (vê os vendedores dele)" },
  { value: "coordenador", label: "Coordenador (vê os supervisores dele)" },
  { value: "gerente", label: "Gerente (vê os coordenadores dele)" },
];

function TeamSection({ isOwner, meuTeamRole, meuId }) {
  const [members, setMembers] = useState([]);
  const [buscaMembro, setBuscaMembro] = useState("");
  const [fotosMembros, setFotosMembros] = useState({}); // { member_id: data-uri } — chega numa 2ª chamada, depois da lista
  const membrosFiltrados = React.useMemo(() => {
    const termo = buscaMembro.trim().toLowerCase();
    if (!termo) return members;
    return members.filter(m =>
      m.name.toLowerCase().includes(termo) ||
      m.email.toLowerCase().includes(termo) ||
      m.role.toLowerCase().includes(termo)
    );
  }, [members, buscaMembro]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "vendedor", whatsapp_phone: "", supervisor_id: "", cpf: "" });

  // Dono da conta (sem team_role) não tem teto; um líder só cria papel
  // estritamente abaixo do próprio nível — mesma regra que o backend valida.
  const meuNivel = meuTeamRole ? (NIVEL_PAPEL[meuTeamRole] ?? 0) : Infinity;
  const opcoesPapelDisponiveis = OPCOES_PAPEL.filter(o => NIVEL_PAPEL[o.value] < meuNivel);

  const fetchMembers = useCallback(async () => {
    try {
      const { data } = await api.get("/team/members");
      setMembers(data);
    } catch (e) { /* provavelmente não é dono nem líder — silencioso */ }
    finally { setLoading(false); }
    // Fotos vêm à parte: a lista já apareceu, as fotos vão "entrando" depois.
    try {
      const { data } = await api.get("/team/members/avatars");
      setFotosMembros(data.avatars || {});
    } catch (e) { /* sem foto = mostra o ícone padrão */ }
  }, []);

  useEffect(() => { if (isOwner) fetchMembers(); else setLoading(false); }, [isOwner, fetchMembers]);

  // Só admin_company/dono — mesma trava do backend. Existe pra recuperar
  // cards que ficaram "perdidos" de um jeito raro: se um líder foi
  // removido ANTES da correção que passou a realocar automaticamente (ou
  // por qualquer outro motivo os dados ficarem com um dono que não existe
  // mais), essa ferramenta devolve eles — e deixa escolher pra qual conta
  // da equipe cada grupo vai (ex: quem assumiu depois que o líder antigo
  // foi desligado), em vez de tudo cair sem escolha na conta geral.
  const souAdminCompany = !meuTeamRole || meuTeamRole === "admin_company";
  const [modalOrfaosAberto, setModalOrfaosAberto] = useState(false);
  const [carregandoOrfaos, setCarregandoOrfaos] = useState(false);
  const [gruposOrfaos, setGruposOrfaos] = useState(null);
  const [destinosOrfaos, setDestinosOrfaos] = useState({}); // { user_id_orfao: conta_destino_id }
  const [reparandoOrfaos, setReparandoOrfaos] = useState(false);

  const abrirModalOrfaos = async () => {
    setModalOrfaosAberto(true);
    setCarregandoOrfaos(true);
    try {
      const { data } = await api.get("/company/pipeline/orfaos-preview");
      setGruposOrfaos(data.grupos || []);
      setDestinosOrfaos({});
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao carregar cards órfãos."));
      setModalOrfaosAberto(false);
    } finally { setCarregandoOrfaos(false); }
  };

  const handleConfirmarReparoOrfaos = async () => {
    setReparandoOrfaos(true);
    try {
      const reatribuicoes = Object.fromEntries(Object.entries(destinosOrfaos).filter(([, v]) => v));
      const { data } = await api.post("/company/pipeline/reparar-cards-orfaos", { reatribuicoes });
      if (data.cards_reparados > 0) {
        toast.success(`${data.cards_reparados} card(s) recuperado(s).`);
      } else {
        toast.success("Nenhum card órfão encontrado — está tudo em dia.");
      }
      setModalOrfaosAberto(false);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao reparar cards órfãos."));
    } finally { setReparandoOrfaos(false); }
  };

  if (!isOwner) return null;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) { toast.error("Nome muito curto."); return; }
    if (form.password.length < 6) { toast.error("Senha precisa ter pelo menos 6 caracteres."); return; }
    setSaving(true);
    try {
      const payload = { ...form, supervisor_id: form.supervisor_id || null };
      await api.post("/team/members", payload);
      toast.success(`${form.name} adicionado(a) como ${form.role}!`);
      setForm({ name: "", email: "", password: "", role: opcoesPapelDisponiveis[0]?.value || "vendedor", whatsapp_phone: "", supervisor_id: "", cpf: "" });
      setShowForm(false);
      fetchMembers();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao criar membro."));
    } finally { setSaving(false); }
  };

  const handleChangeSupervisor = async (m, novoSupervisorId) => {
    try {
      await api.patch(`/team/members/${m.id}`, { supervisor_id: novoSupervisorId || null });
      setMembers(prev => prev.map(x => x.id === m.id ? { ...x, supervisor_id: novoSupervisorId || null } : x));
      toast.success("Hierarquia atualizada.");
    } catch (e) { toast.error(getErrorMessage(e, "Erro ao atualizar hierarquia.")); }
  };

  const handleToggleActive = async (m) => {
    try {
      await api.patch(`/team/members/${m.id}`, { active: !m.active });
      setMembers(prev => prev.map(x => x.id === m.id ? { ...x, active: !x.active } : x));
    } catch (e) { toast.error(getErrorMessage(e, "Erro ao atualizar.")); }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Remover ${m.name} da equipe? Essa conta perde o acesso.`)) return;
    try {
      await api.delete(`/team/members/${m.id}`);
      setMembers(prev => prev.filter(x => x.id !== m.id));
      toast.success("Membro removido.");
    } catch (e) { toast.error(getErrorMessage(e, "Erro ao remover.")); }
  };

  // Quem pode aparecer como "reporta para": eu mesmo (se sou líder, não
  // dono) + qualquer membro já cadastrado que não seja vendedor/bko (só
  // quem lidera gente pode ser escolhido como superior).
  const opcoesSupervisor = [
    ...(meuTeamRole ? [{ id: meuId, name: "Eu mesmo" }] : []),
    ...members.filter(m => (NIVEL_PAPEL[m.role] ?? 0) > 0),
  ];

  return (
    <div style={{ background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 14, padding: 18, marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: "hsl(var(--foreground))", display: "flex", alignItems: "center", gap: 8 }}>
          <Users2 size={17} color="#a855f7" /> Equipe
        </h3>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {souAdminCompany && (
            <button
              onClick={abrirModalOrfaos}
              disabled={reparandoOrfaos}
              style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 8, border: "1px solid hsl(var(--border))", background: "none", color: "hsl(var(--muted-foreground))", fontSize: 12.5, fontWeight: 600, cursor: "pointer", opacity: reparandoOrfaos ? 0.6 : 1 }}
            >
              <ArrowRightLeft size={14} /> Reparar Cards Órfãos
            </button>
          )}
          <button
            onClick={() => setShowForm(s => !s)}
            style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 8, border: "1px solid #a855f7", background: showForm ? "transparent" : "rgba(168,85,247,0.1)", color: "#a855f7", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
          >
            {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "Cancelar" : "Adicionar membro"}
          </button>
        </div>
      </div>
      {souAdminCompany && (
        <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginTop: -6, marginBottom: 14 }}>
          Esse recurso é exclusivo para organizar os cards que estavam abaixo do líder retirado do sistema.
        </div>
      )}

      {showForm && (
        <form onSubmit={handleCreate} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16, padding: 14, background: "hsl(var(--background))", borderRadius: 10, border: "1px solid hsl(var(--border))" }}>
          <input placeholder="Nome" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            style={{ padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13 }} />
          <input placeholder="E-mail" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            style={{ padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13 }} />
          <input placeholder="Senha (mín. 6 caracteres)" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            style={{ padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13 }} />
          <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
            style={{ padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13 }}>
            {opcoesPapelDisponiveis.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={form.supervisor_id} onChange={e => setForm(f => ({ ...f, supervisor_id: e.target.value }))}
            style={{ padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13, gridColumn: meuTeamRole ? "auto" : "1 / -1" }}>
            <option value="">{meuTeamRole ? "Reporta para: eu mesmo (padrão)" : "Reporta para: ninguém (topo da hierarquia)"}</option>
            {opcoesSupervisor.map(o => <option key={o.id} value={o.id}>Reporta para: {o.name}</option>)}
          </select>
          <input placeholder="WhatsApp pessoal (com DDI+DDD, ex: 5511999999999, opcional)" value={form.whatsapp_phone} onChange={e => setForm(f => ({ ...f, whatsapp_phone: e.target.value }))}
            style={{ padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13, gridColumn: "1 / -1" }} />
          <CpfInput value={form.cpf} onChange={e => setForm(f => ({ ...f, cpf: e.target.value }))} style={{ gridColumn: "1 / -1" }} />
          <div style={{ gridColumn: "1 / -1", fontSize: 10.5, color: "hsl(var(--muted-foreground))", marginTop: -4 }}>
            💡 "Reporta para" define a hierarquia: quem está acima só enxerga as vendas/indicadores de quem reporta pra ele (direto ou em cadeia). Se for vendedor e conectar o WhatsApp próprio na aba WhatsApp, esse número recebe aviso automático quando um pedido dele voltar "Para Correção". Inclua o 55 na frente (DDI Brasil), senão o aviso não chega. O CPF, se preenchido, é usado só pra casar com a planilha na sincronização — fica guardado com hash, nunca em texto puro.
          </div>
          <button type="submit" disabled={saving} style={{ gridColumn: "1 / -1", padding: "9px", borderRadius: 7, border: "none", background: "#a855f7", color: "#fff", fontSize: 13, fontWeight: 700, cursor: saving ? "default" : "pointer" }}>
            {saving ? "Criando..." : "Criar conta de equipe"}
          </button>
        </form>
      )}

      {loading ? (
        <Loader2 className="animate-spin" size={16} color="hsl(var(--muted-foreground))" />
      ) : members.length === 0 ? (
        <p style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>Nenhum membro ainda — você é o único que enxerga o pipeline por enquanto.</p>
      ) : (
        <>
          <input
            value={buscaMembro}
            onChange={e => setBuscaMembro(e.target.value)}
            placeholder="Pesquisar conta (nome, e-mail ou papel)..."
            style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 12.5, marginBottom: 8 }}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 420, overflowY: "auto", paddingRight: 4 }}>
            {membrosFiltrados.length === 0 ? (
              <p style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))", padding: "8px 0" }}>Nenhuma conta encontrada com esse termo.</p>
            ) : membrosFiltrados.map(m => (
            <div key={m.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", opacity: m.active ? 1 : 0.5, flexWrap: "wrap", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                {fotosMembros[m.id] ? (
                  <img src={fotosMembros[m.id]} alt="" style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(168,85,247,0.14)", color: "#a855f7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                    {(m.name || "?").trim().charAt(0).toUpperCase()}
                  </div>
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: "hsl(var(--foreground))", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.name}</div>
                  <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>{m.email} · {m.role}{m.cpf_ultimos3 ? ` · CPF ...${m.cpf_ultimos3}` : ""}</div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <select value={m.supervisor_id || ""} onChange={e => handleChangeSupervisor(m, e.target.value)} title="Reporta para"
                  style={{ fontSize: 10.5, padding: "3px 6px", borderRadius: 6, border: "1px solid hsl(var(--border))", background: "transparent", color: "hsl(var(--muted-foreground))" }}>
                  <option value="">Reporta para: ninguém</option>
                  {[...(meuTeamRole ? [{ id: meuId, name: "Eu mesmo" }] : []), ...members.filter(x => x.id !== m.id && (NIVEL_PAPEL[x.role] ?? 0) > 0)]
                    .map(o => <option key={o.id} value={o.id}>Reporta para: {o.name}</option>)}
                </select>
                <button onClick={() => handleToggleActive(m)} title={m.active ? "Desativar acesso" : "Reativar acesso"}
                  style={{ fontSize: 10.5, padding: "3px 8px", borderRadius: 6, border: "1px solid hsl(var(--border))", background: "transparent", color: "hsl(var(--muted-foreground))", cursor: "pointer" }}>
                  {m.active ? "Ativo" : "Inativo"}
                </button>
                <button onClick={() => handleDelete(m)} style={{ padding: 4, border: "none", background: "transparent", color: "#ef4444", cursor: "pointer" }}>
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
          </div>
        </>
      )}

      {modalOrfaosAberto && (
        <div onClick={() => !reparandoOrfaos && setModalOrfaosAberto(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "hsl(var(--background))", borderRadius: 14, padding: 22, width: "100%", maxWidth: 560, maxHeight: "80vh", overflow: "auto" }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Reparar Cards Órfãos</div>
            <div style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", marginBottom: 16 }}>
              Esse recurso é exclusivo para organizar os cards que estavam abaixo do líder retirado do sistema.
            </div>

            {carregandoOrfaos ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 30 }}><Loader2 className="animate-spin" size={20} color="hsl(var(--muted-foreground))" /></div>
            ) : !gruposOrfaos || gruposOrfaos.length === 0 ? (
              <div style={{ fontSize: 13, color: "hsl(var(--muted-foreground))", padding: "10px 0" }}>Nenhum card órfão encontrado — está tudo em dia.</div>
            ) : (
              <>
                <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", marginBottom: 12 }}>
                  Escolha pra qual conta da equipe cada grupo vai (ex: quem assumiu no lugar do líder que saiu). Se deixar em branco, vai pra conta geral.
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {gruposOrfaos.map(g => (
                    <div key={g.user_id_orfao} style={{ border: "1px solid hsl(var(--border))", borderRadius: 10, padding: 12 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 2 }}>
                        {g.quantidade_cards} card(s) de uma conta removida
                      </div>
                      {(g.pista_vendedor || g.pista_supervisor) && (
                        <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginBottom: 8 }}>
                          {g.pista_vendedor && <>Vendedor mais comum nesses cards: <strong>{g.pista_vendedor}</strong>. </>}
                          {g.pista_supervisor && <>Supervisor mais comum: <strong>{g.pista_supervisor}</strong>.</>}
                        </div>
                      )}
                      <select
                        value={destinosOrfaos[g.user_id_orfao] || ""}
                        onChange={e => setDestinosOrfaos(prev => ({ ...prev, [g.user_id_orfao]: e.target.value }))}
                        style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 12.5 }}
                      >
                        <option value="">Conta geral (padrão)</option>
                        {members.filter(m => m.active).map(m => (
                          <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
              <button onClick={() => setModalOrfaosAberto(false)} disabled={reparandoOrfaos}
                style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid hsl(var(--border))", background: "none", color: "hsl(var(--foreground))", fontSize: 13, cursor: "pointer" }}>
                Cancelar
              </button>
              {gruposOrfaos && gruposOrfaos.length > 0 && (
                <button onClick={handleConfirmarReparoOrfaos} disabled={reparandoOrfaos}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "none", background: "#a855f7", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: reparandoOrfaos ? 0.6 : 1 }}>
                  {reparandoOrfaos && <Loader2 size={14} className="animate-spin" />} Confirmar reparo
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════ Parceiros autorizados (NOVO PEDIDO via WhatsApp) ═══════════════════
function ParceirosSection({ podeGerenciar }) {
  const [parceiros, setParceiros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [expandido, setExpandido] = useState(false);

  const fetchParceiros = useCallback(async () => {
    try {
      const { data } = await api.get("/company/pipeline/parceiros");
      setParceiros(data.parceiros);
    } catch (e) { /* provavelmente vendedor — silencioso */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (podeGerenciar) fetchParceiros(); else setLoading(false); }, [podeGerenciar, fetchParceiros]);

  if (!podeGerenciar) return null;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) { toast.error("Nome muito curto."); return; }
    if (form.phone.replace(/\D/g, "").length < 10) { toast.error("Telefone inválido — inclui DDD."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/company/pipeline/parceiros", form);
      setParceiros(prev => [data.parceiro, ...prev]);
      toast.success(`${form.name} autorizado a criar pedidos via WhatsApp!`);
      setForm({ name: "", phone: "" });
      setShowForm(false);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao cadastrar parceiro."));
    } finally { setSaving(false); }
  };

  const handleToggleActive = async (p) => {
    try {
      await api.patch(`/company/pipeline/parceiros/${p.id}`, { active: !p.active });
      setParceiros(prev => prev.map(x => x.id === p.id ? { ...x, active: !x.active } : x));
    } catch (e) { toast.error(getErrorMessage(e, "Erro ao atualizar.")); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Remover ${p.name} da lista de parceiros autorizados?`)) return;
    try {
      await api.delete(`/company/pipeline/parceiros/${p.id}`);
      setParceiros(prev => prev.filter(x => x.id !== p.id));
      toast.success("Parceiro removido.");
    } catch (e) { toast.error(getErrorMessage(e, "Erro ao remover.")); }
  };

  return (
    <div style={{ background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 14, padding: 18, marginBottom: 24 }}>
      <div onClick={() => setExpandido(x => !x)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: "hsl(var(--foreground))", display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
          <Phone size={17} color="#a855f7" /> Parceiros autorizados (WhatsApp) <span style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", fontWeight: 500 }}>({parceiros.length})</span>
        </h3>
        <ChevronDown size={16} color="hsl(var(--muted-foreground))" style={{ transform: expandido ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
      </div>

      {expandido && (
        <>
          <p style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", marginTop: 10, marginBottom: 14 }}>
            Números cadastrados aqui podem criar pedidos direto pelo WhatsApp de atendimento, mandando
            <code style={{ margin: "0 4px", padding: "1px 6px", background: "hsl(var(--background))", borderRadius: 5 }}>NOVO PEDIDO</code>
            seguido de Nome, Telefone, CNPJ, Plano e Valor — depois manda a foto do documento que o sistema anexa e envia pro Enviado BKO sozinho.
            Sem custo de login — só o número precisa estar nessa lista, ativo, <strong>com o 55 na frente</strong> (senão o sistema não reconhece a mensagem).
          </p>

          <button onClick={() => setShowForm(s => !s)}
            style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 8, border: "1px solid #a855f7", background: showForm ? "transparent" : "rgba(168,85,247,0.1)", color: "#a855f7", fontSize: 12.5, fontWeight: 600, cursor: "pointer", marginBottom: 14 }}>
            {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "Cancelar" : "Autorizar novo número"}
          </button>

          {showForm && (
            <form onSubmit={handleCreate} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, marginBottom: 16, padding: 14, background: "hsl(var(--background))", borderRadius: 10, border: "1px solid hsl(var(--border))", alignItems: "end" }}>
              <label style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>Nome do parceiro
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13 }} />
              </label>
              <label style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>WhatsApp (com DDI+DDD)
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="5511999998888"
                  style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13 }} />
              </label>
              <button type="submit" disabled={saving} style={{ padding: "9px 16px", borderRadius: 7, border: "none", background: "#a855f7", color: "#fff", fontSize: 13, fontWeight: 700, cursor: saving ? "default" : "pointer", height: 36 }}>
                {saving ? "Salvando..." : "Autorizar"}
              </button>
            </form>
          )}

          {loading ? (
            <Loader2 className="animate-spin" size={16} color="hsl(var(--muted-foreground))" />
          ) : parceiros.length === 0 ? (
            <p style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>Nenhum parceiro autorizado ainda.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {parceiros.map(p => (
                <div key={p.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", opacity: p.active ? 1 : 0.5 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <Phone size={14} color="#a855f7" />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: "hsl(var(--foreground))" }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>{p.phone}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    <button onClick={() => handleToggleActive(p)} title={p.active ? "Desativar" : "Reativar"}
                      style={{ fontSize: 10.5, padding: "3px 8px", borderRadius: 6, border: "1px solid hsl(var(--border))", background: "transparent", color: "hsl(var(--muted-foreground))", cursor: "pointer" }}>
                      {p.active ? "Ativo" : "Inativo"}
                    </button>
                    <button onClick={() => handleDelete(p)} style={{ padding: 4, border: "none", background: "transparent", color: "#ef4444", cursor: "pointer" }}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════ Card ═══════════════════
function Card({ lead, onMove, onDelete, onDeleteColuna, onDragStart, onEdit, teamRole, isAdminCompany }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const isVendedor = teamRole === "vendedor";

  return (
    <div
      draggable
      onDragStart={(e) => { setDragging(true); onDragStart(lead.id); e.dataTransfer.effectAllowed = "move"; }}
      onDragEnd={() => setDragging(false)}
      style={{
        background: dragging ? "rgba(168,85,247,0.08)" : "hsl(var(--background))",
        border: `1px solid ${dragging ? "#a855f7" : "hsl(var(--border))"}`,
        borderRadius: 10, padding: 12, marginBottom: 8, position: "relative",
        cursor: "grab", opacity: dragging ? 0.5 : 1, userSelect: "none",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <GripVertical size={12} color="hsl(var(--muted-foreground))" style={{ opacity: 0.5, flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            {lead.numero && <div style={{ fontSize: 10, color: "#a855f7", fontWeight: 700 }}>#{lead.numero}</div>}
            <div style={{ fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{lead.name}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
          <button onClick={(e) => { e.stopPropagation(); onEdit(lead); }} title="Editar / ver histórico"
            style={{ background: "none", border: "none", cursor: "pointer", color: "hsl(var(--muted-foreground))", padding: 2 }}>
            <Pencil size={13} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o); }} style={{ background: "none", border: "none", cursor: "pointer", color: "hsl(var(--muted-foreground))" }}>
            <MoreVertical size={14} />
          </button>
        </div>
      </div>

      {lead.checklist?.numero_venda && (
        <div style={{ fontSize: 10.5, color: "#16a34a", fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 4, background: "rgba(22,163,74,0.1)", border: "1px solid rgba(22,163,74,0.25)", borderRadius: 6, padding: "3px 7px", width: "fit-content" }}>
          🎟️ Venda {lead.checklist.numero_venda}
        </div>
      )}

      {lead.phone && <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}><Phone size={10} /> {lead.phone}</div>}
      {lead.cnpj && <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}><Building2 size={10} /> {lead.cnpj}</div>}
      {lead.plano_negociado && <div style={{ fontSize: 11, color: "#60a5fa", marginBottom: 4, fontWeight: 600 }}>📶 {lead.plano_negociado}</div>}
      {lead.value > 0 && <div style={{ fontSize: 11.5, color: "#22c55e", marginBottom: 4, fontWeight: 700 }}>💰 {fmtBRL(lead.value)}</div>}
      {lead.vendedor_nome && (
        <div style={{ fontSize: 10.5, color: "#a855f7", fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 4, background: "rgba(168,85,247,0.1)", border: "1px solid rgba(168,85,247,0.25)", borderRadius: 6, padding: "3px 7px", width: "fit-content" }}>
          👤 {lead.vendedor_nome}
        </div>
      )}
      {lead.data_instalacao && <div style={{ fontSize: 10.5, color: "#16a34a", marginBottom: 4, fontWeight: 600 }}>📅 {lead.data_instalacao}</div>}
      {lead.nota_bko && <div title={lead.nota_bko} style={{ fontSize: 10.5, color: "#f97316", marginBottom: 4, fontStyle: "italic", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>💬 {lead.nota_bko}</div>}
      {lead.anexos?.length > 0 && (
        <div style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))", display: "flex", alignItems: "center", gap: 4 }}>
          <Paperclip size={10} /> {lead.anexos.length} anexo{lead.anexos.length > 1 ? "s" : ""}
        </div>
      )}

      {menuOpen && (
        <div style={{ position: "absolute", top: 32, right: 8, zIndex: 20, background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 10, overflow: "hidden", minWidth: 170, boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
          {COLS.filter(c => c.id !== lead.col && !(isVendedor && c.id === "concluido")).map(c => (
            <button key={c.id} onClick={() => { onMove(lead.id, c.id); setMenuOpen(false); }}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", fontSize: 12, background: "none", border: "none", color: "hsl(var(--foreground))", cursor: "pointer" }}>
              Mover pra {c.label}
            </button>
          ))}
          <button onClick={() => { if (window.confirm("Remover esse card?")) onDelete(lead.id); setMenuOpen(false); }}
            style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", fontSize: 12, background: "none", border: "none", borderTop: "1px solid hsl(var(--border))", color: "#ef4444", cursor: "pointer" }}>
            Remover card
          </button>
          {isAdminCompany && (
            <button onClick={() => { onDeleteColuna(lead.col); setMenuOpen(false); }}
              title={`Apagar todos os cards da coluna "${colLabel(lead.col)}"`}
              style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", textAlign: "left", padding: "8px 12px", fontSize: 12, background: "none", border: "none", borderTop: "1px solid hsl(var(--border))", color: "#ef4444", cursor: "pointer" }}>
              <Trash2 size={12} /> Apagar todos da coluna
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function colLabel(id) {
  return COLS.find(c => c.id === id)?.label || id || "—";
}

function fmtData(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

// ═══════════════════ Modal de edição + histórico ═══════════════════
function EditLeadModal({ lead, teamRole, isAdminCompany, onClose, onSaved }) {
  const isVendedor = teamRole === "vendedor";
  const [tab, setTab] = useState("editar"); // "editar" | "checklist" | "historico" | "anexos"
  const [form, setForm] = useState({
    name: lead.name || "", phone: lead.phone || "", cnpj: lead.cnpj || "",
    plano_negociado: lead.plano_negociado || "", value: lead.value || 0,
    observacoes: lead.observacoes || "",
    valor_pago_vendedor: lead.valor_pago_vendedor || 0,
    valor_recebido_operadora: lead.valor_recebido_operadora || 0,
  });
  const [saving, setSaving] = useState(false);
  const [checklistForm, setChecklistForm] = useState({ ...(lead.checklist || {}) });
  const [savingChecklist, setSavingChecklist] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);

  const handleChecklistChange = async (key, value) => {
    setChecklistForm(f => ({ ...f, [key]: value }));

    if (key !== "cep") return;
    const digits = value.replace(/\D/g, "");
    if (digits.length !== 8) return;

    setBuscandoCep(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await resp.json();
      if (data.erro) {
        toast.error("CEP não encontrado.");
        return;
      }
      setChecklistForm(f => ({
        ...f,
        logradouro: data.logradouro || f.logradouro,
        bairro: data.bairro || f.bairro,
        cidade: data.localidade || f.cidade,
        uf: data.uf || f.uf,
      }));
      toast.success("Endereço preenchido automaticamente!");
    } catch (e) {
      toast.error("Erro ao consultar o CEP — preenche manualmente.");
    } finally { setBuscandoCep(false); }
  };
  const [anexos, setAnexos] = useState(lead.anexos || []);
  const [uploadingAnexo, setUploadingAnexo] = useState(false);
  const anexoInputRef = useRef(null);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/company/pipeline/${lead.id}/details`, {
        ...form, value: Number(form.value) || 0,
        valor_pago_vendedor: Number(form.valor_pago_vendedor) || 0,
        valor_recebido_operadora: Number(form.valor_recebido_operadora) || 0,
      });
      toast.success("Card atualizado!");
      onSaved(data.lead);
      onClose();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao salvar."));
    } finally { setSaving(false); }
  };

  const handleSaveChecklist = async (e) => {
    e.preventDefault();
    setSavingChecklist(true);
    try {
      const { data } = await api.post(`/company/pipeline/${lead.id}/details`, { checklist: checklistForm });
      toast.success("Checklist atualizado!");
      onSaved(data.lead);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao salvar checklist."));
    } finally { setSavingChecklist(false); }
  };

  const handleUploadAnexo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Arquivo muito grande — máximo 10MB."); return; }

    setUploadingAnexo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(`/company/pipeline/${lead.id}/anexos`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const novosAnexos = [...anexos, data.anexo];
      setAnexos(novosAnexos);
      onSaved({ ...lead, anexos: novosAnexos });
      toast.success("Anexo adicionado!");
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao anexar arquivo."));
    } finally { setUploadingAnexo(false); }
  };

  const [openingAnexoId, setOpeningAnexoId] = useState(null);
  const handleOpenAnexo = async (anexoId) => {
    // Os links do R2 não duram mais que 7 dias (limite do protocolo S3) —
    // então geramos um link novo (1h) na hora que a pessoa clica, em vez
    // de guardar um link fixo desde o upload.
    setOpeningAnexoId(anexoId);
    try {
      const { data } = await api.get(`/company/pipeline/${lead.id}/anexos/${anexoId}/url`);
      window.open(data.url, "_blank");
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao abrir o anexo."));
    } finally { setOpeningAnexoId(null); }
  };

  const handleDeleteAnexo = async (anexoId) => {
    if (!window.confirm("Remover esse anexo?")) return;
    try {
      await api.delete(`/company/pipeline/${lead.id}/anexos/${anexoId}`);
      const novosAnexos = anexos.filter(a => a.id !== anexoId);
      setAnexos(novosAnexos);
      onSaved({ ...lead, anexos: novosAnexos });
      toast.success("Anexo removido.");
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao remover anexo."));
    }
  };

  const historico = [...(lead.historico || [])].reverse(); // mais recente primeiro

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 14, width: "100%", maxWidth: 480, maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid hsl(var(--border))" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "hsl(var(--foreground))" }}>
              {lead.numero && <span style={{ color: "#a855f7" }}>#{lead.numero} · </span>}{lead.name}
            </div>
            <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>{colLabel(lead.col)}</div>
            {lead.checklist?.numero_venda && (
              <div style={{ fontSize: 11, color: "#16a34a", fontWeight: 700, marginTop: 3 }}>
                🎟️ Venda emitida: {lead.checklist.numero_venda}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "hsl(var(--muted-foreground))" }}><X size={18} /></button>
        </div>

        <div style={{ display: "flex", borderBottom: "1px solid hsl(var(--border))" }}>
          <button onClick={() => setTab("editar")} style={{ flex: 1, padding: "10px", fontSize: 12.5, fontWeight: 600, background: "none", border: "none", borderBottom: tab === "editar" ? "2px solid #a855f7" : "2px solid transparent", color: tab === "editar" ? "#a855f7" : "hsl(var(--muted-foreground))", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <Pencil size={13} /> Editar
          </button>
          <button onClick={() => setTab("checklist")} style={{ flex: 1, padding: "10px", fontSize: 12.5, fontWeight: 600, background: "none", border: "none", borderBottom: tab === "checklist" ? "2px solid #a855f7" : "2px solid transparent", color: tab === "checklist" ? "#a855f7" : "hsl(var(--muted-foreground))", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <ClipboardList size={13} /> Checklist
          </button>
          <button onClick={() => setTab("historico")} style={{ flex: 1, padding: "10px", fontSize: 12.5, fontWeight: 600, background: "none", border: "none", borderBottom: tab === "historico" ? "2px solid #a855f7" : "2px solid transparent", color: tab === "historico" ? "#a855f7" : "hsl(var(--muted-foreground))", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <History size={13} /> Histórico ({historico.length})
          </button>
          <button onClick={() => setTab("anexos")} style={{ flex: 1, padding: "10px", fontSize: 12.5, fontWeight: 600, background: "none", border: "none", borderBottom: tab === "anexos" ? "2px solid #a855f7" : "2px solid transparent", color: tab === "anexos" ? "#a855f7" : "hsl(var(--muted-foreground))", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <Paperclip size={13} /> Anexos ({anexos.length})
          </button>
        </div>

        <div style={{ padding: 18, overflowY: "auto" }}>
          {tab === "editar" ? (
            <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <label style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>Nome
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13 }} />
              </label>
              <label style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>Telefone
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13 }} />
              </label>
              <label style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>CNPJ
                <input value={form.cnpj} onChange={e => setForm(f => ({ ...f, cnpj: e.target.value }))}
                  style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13 }} />
              </label>
              <label style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>Plano negociado
                <input value={form.plano_negociado} onChange={e => setForm(f => ({ ...f, plano_negociado: e.target.value }))}
                  style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13 }} />
              </label>
              <label style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>Valor (R$)
                <input type="number" step="0.01" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
                  style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13 }} />
              </label>
              <label style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>Observações
                <textarea rows={4} value={form.observacoes} onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))}
                  placeholder="Anotações gerais sobre esse pedido — status, pendências, o que foi combinado com o cliente..."
                  style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13, resize: "vertical" }} />
              </label>
              {isAdminCompany && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 10, padding: 10 }}>
                  <div style={{ gridColumn: "1 / -1", fontSize: 10.5, fontWeight: 700, color: "#4ade80", textTransform: "uppercase" }}>
                    Financeiro — só admin da empresa vê
                  </div>
                  <label style={{ fontSize: 10.5, color: "#86efac" }}>Pago ao vendedor (R$)
                    <input type="number" step="0.01" value={form.valor_pago_vendedor} onChange={e => setForm(f => ({ ...f, valor_pago_vendedor: e.target.value }))}
                      style={{ display: "block", width: "100%", marginTop: 3, padding: "6px 8px", borderRadius: 6, border: "1px solid rgba(34,197,94,0.3)", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 12 }} />
                  </label>
                  <label style={{ fontSize: 10.5, color: "#86efac" }}>Recebido da operadora (R$)
                    <input type="number" step="0.01" value={form.valor_recebido_operadora} onChange={e => setForm(f => ({ ...f, valor_recebido_operadora: e.target.value }))}
                      style={{ display: "block", width: "100%", marginTop: 3, padding: "6px 8px", borderRadius: 6, border: "1px solid rgba(34,197,94,0.3)", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 12 }} />
                  </label>
                </div>
              )}
              <button type="submit" disabled={saving} style={{ marginTop: 4, padding: "10px", borderRadius: 8, border: "none", background: "#a855f7", color: "#fff", fontSize: 13.5, fontWeight: 700, cursor: saving ? "default" : "pointer" }}>
                {saving ? "Salvando..." : "Salvar alterações"}
              </button>
            </form>
          ) : tab === "checklist" ? (
            <form onSubmit={handleSaveChecklist} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", marginBottom: 8 }}>
                  Preenchimento vendedor
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {CHECKLIST_CAMPOS_VENDEDOR.map(([key, label]) => (
                    <label key={key} style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))" }}>
                      {label}{key === "cep" && buscandoCep && <span style={{ color: "#a855f7", marginLeft: 5 }}>(buscando endereço...)</span>}
                      <input
                        value={checklistForm[key] || ""}
                        onChange={e => handleChecklistChange(key, e.target.value)}
                        maxLength={key === "cep" ? 9 : undefined}
                        placeholder={key === "cep" ? "00000-000" : undefined}
                        style={{ display: "block", width: "100%", marginTop: 3, padding: "6px 8px", borderRadius: 6, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 12 }}
                      />
                    </label>
                  ))}
                  <label style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))" }}>
                    Forma de Pagamento
                    <select
                      value={checklistForm.forma_pagamento || ""}
                      onChange={e => handleChecklistChange("forma_pagamento", e.target.value)}
                      style={{ display: "block", width: "100%", marginTop: 3, padding: "6px 8px", borderRadius: 6, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 12 }}
                    >
                      <option value="">— selecione —</option>
                      <option value="Boleto ou débito em conta">Boleto ou débito em conta</option>
                      <option value="Débito automático">Débito automático</option>
                      <option value="Cartão de crédito">Cartão de crédito</option>
                    </select>
                  </label>
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: "#f87171", textTransform: "uppercase", marginBottom: 8 }}>
                  Preenchimento backoffice{isVendedor && <span style={{ color: "#fca5a5", fontWeight: 500, textTransform: "none" }}> — somente leitura</span>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, background: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 10, padding: 10, opacity: isVendedor ? 0.55 : 1 }}>
                  {CHECKLIST_CAMPOS_BACKOFFICE.map(([key, label]) => (
                    <label key={key} style={{ fontSize: 10.5, color: "#fca5a5" }}>
                      {label}
                      <input
                        value={checklistForm[key] || ""}
                        onChange={e => setChecklistForm(f => ({ ...f, [key]: e.target.value }))}
                        disabled={isVendedor}
                        style={{ display: "block", width: "100%", marginTop: 3, padding: "6px 8px", borderRadius: 6, border: "1px solid rgba(248,113,113,0.3)", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 12, cursor: isVendedor ? "not-allowed" : "text" }}
                      />
                    </label>
                  ))}
                </div>
              </div>

              <button type="submit" disabled={savingChecklist} style={{ padding: "10px", borderRadius: 8, border: "none", background: "#a855f7", color: "#fff", fontSize: 13.5, fontWeight: 700, cursor: savingChecklist ? "default" : "pointer" }}>
                {savingChecklist ? "Salvando..." : "Salvar checklist"}
              </button>
            </form>
          ) : tab === "historico" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {historico.length === 0 && <p style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>Nenhuma movimentação registrada ainda.</p>}
              {historico.map((h, i) => (
                <div key={i} style={{ padding: "10px 12px", borderRadius: 8, background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "hsl(var(--foreground))" }}>{h.usuario_nome || "—"}</span>
                    <span style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))" }}>{fmtData(h.data)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "#a855f7", marginBottom: h.nota ? 6 : 0 }}>
                    {h.tipo === "observacao"
                      ? "Editou as observações"
                      : h.tipo === "checklist"
                      ? "Atualizou o checklist"
                      : h.de ? `Moveu de "${colLabel(h.de)}" para "${colLabel(h.para)}"` : `Criou o card em "${colLabel(h.para)}"`}
                  </div>
                  {h.nota && <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", fontStyle: "italic" }}>💬 {h.nota}</div>}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <input type="file" ref={anexoInputRef} onChange={handleUploadAnexo}
                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx" style={{ display: "none" }} />
              <button
                type="button" onClick={() => anexoInputRef.current?.click()} disabled={uploadingAnexo}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px", borderRadius: 8, border: "1px dashed #a855f7", background: "rgba(168,85,247,0.06)", color: "#a855f7", fontSize: 13, fontWeight: 600, cursor: uploadingAnexo ? "default" : "pointer" }}
              >
                <Upload size={14} /> {uploadingAnexo ? "Enviando..." : "Anexar arquivo (PDF, imagem, Word, Excel — máx. 10MB)"}
              </button>

              {anexos.length === 0 ? (
                <p style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))", textAlign: "center", padding: "8px 0" }}>Nenhum anexo ainda.</p>
              ) : (
                anexos.map(a => (
                  <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))" }}>
                    <FileText size={16} color="#a855f7" style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: "hsl(var(--foreground))", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.nome_original}</div>
                      <div style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))" }}>{a.enviado_por} · {fmtData(a.enviado_em)}</div>
                    </div>
                    <button onClick={() => handleOpenAnexo(a.id)} disabled={openingAnexoId === a.id} title="Baixar/abrir"
                      style={{ background: "none", border: "none", cursor: openingAnexoId === a.id ? "default" : "pointer", color: "hsl(var(--muted-foreground))", flexShrink: 0 }}>
                      {openingAnexoId === a.id ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                    </button>
                    <button onClick={() => handleDeleteAnexo(a.id)} title="Remover"
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", flexShrink: 0 }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function NewLeadForm({ onCreated, onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", cnpj: "", plano_negociado: "", value: "" });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 1) { toast.error("Nome é obrigatório."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/company/pipeline", { ...form, value: Number(form.value) || 0 });
      toast.success("Lead adicionado!");
      onCreated(data.lead);
      onClose();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao criar lead."));
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16, padding: 14, background: "hsl(var(--secondary))", borderRadius: 10, border: "1px solid hsl(var(--border))" }}>
      <input placeholder="Nome do cliente *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
        style={{ padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 13 }} />
      <input placeholder="Telefone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
        style={{ padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 13 }} />
      <input placeholder="CNPJ" value={form.cnpj} onChange={e => setForm(f => ({ ...f, cnpj: e.target.value }))}
        style={{ padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 13 }} />
      <input placeholder="Plano negociado" value={form.plano_negociado} onChange={e => setForm(f => ({ ...f, plano_negociado: e.target.value }))}
        style={{ padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 13 }} />
      <input placeholder="Valor (R$)" type="number" step="0.01" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
        style={{ padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 13 }} />
      <button type="submit" disabled={saving} style={{ padding: "9px", borderRadius: 7, border: "none", background: "#a855f7", color: "#fff", fontSize: 13, fontWeight: 700, cursor: saving ? "default" : "pointer" }}>
        {saving ? "Criando..." : "Adicionar ao pipeline"}
      </button>
    </form>
  );
}

// ═══════════════════ Página principal ═══════════════════
// ═══════════════════ Modal de observação ao mover ═══════════════════
function MoveNoteModal({ col, needsData, onConfirm, onCancel }) {
  const [nota, setNota] = useState("");
  const [dataInstalacao, setDataInstalacao] = useState("");
  const info = COLS.find(c => c.id === col);

  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 14, width: "100%", maxWidth: 420 }}>
        <div style={{ padding: "16px 18px", borderBottom: "1px solid hsl(var(--border))", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 9, height: 9, borderRadius: 999, background: info?.color || "#a855f7", flexShrink: 0 }} />
          <div style={{ fontSize: 14.5, fontWeight: 700, color: "hsl(var(--foreground))" }}>Movendo pra "{info?.label || col}"</div>
        </div>
        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>
            Observação (motivo, o que aconteceu com o pedido)
            <textarea
              autoFocus rows={3} value={nota} onChange={(e) => setNota(e.target.value)}
              placeholder="Ex: cliente pediu pra reagendar a instalação pra semana que vem..."
              style={{ display: "block", width: "100%", marginTop: 5, padding: "9px 11px", borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13, resize: "vertical" }}
            />
          </label>
          {needsData && (
            <label style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>
              Data de instalação/reagendamento
              <input
                value={dataInstalacao} onChange={(e) => setDataInstalacao(e.target.value)}
                placeholder="Ex: 15/08/2026"
                style={{ display: "block", width: "100%", marginTop: 5, padding: "9px 11px", borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13 }}
              />
            </label>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, padding: "0 18px 18px" }}>
          <button onClick={onCancel} style={{ flex: 1, padding: "9px", borderRadius: 8, border: "1px solid hsl(var(--border))", background: "transparent", color: "hsl(var(--foreground))", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Cancelar
          </button>
          <button onClick={() => onConfirm({ nota: nota.trim(), data_instalacao: dataInstalacao.trim() })}
            style={{ flex: 1, padding: "9px", borderRadius: 8, border: "none", background: "#a855f7", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════ Histórico de Vendas ═══════════════════
const HistoricoVendas = React.forwardRef(function HistoricoVendas({ onEdit }, ref) {
  const { user } = useAuth();
  const [dataDe, setDataDe] = useState("");
  const [dataAte, setDataAte] = useState("");
  const [busca, setBusca] = useState("");
  const [colunasFiltro, setColunasFiltro] = useState(new Set());
  const [leads, setLeads] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(false);
  // Colunas dinâmicas espelhando o que a empresa mapeou na Sincronização de
  // Base (ex: UF do Cliente, Regional, Portabilidade...) — o backend já
  // devolve só as que estão de fato mapeadas, então a tabela nem precisa
  // saber a lista inteira de campos possíveis, só renderizar o que vier.
  const [colunasAtivas, setColunasAtivas] = useState([]);
  // "updated_at" = quando o CARD foi tocado por último (inclui qualquer
  // toque da Sincronização de Base); "dt_instalacao" = a data real de
  // instalação que veio da coluna "DT Instalação" da planilha. São coisas
  // bem diferentes — por isso vira um seletor visível, não um detalhe
  // escondido, pra não confundir de novo qual data o filtro está usando.
  const [campoData, setCampoData] = useState("updated_at");
  // Filtro por vendedor só faz sentido pra quem lidera gente (supervisor
  // pra cima) — vendedor já só vê os próprios cards, filtrar por si mesmo
  // não ajuda em nada.
  const podeFiltrarVendedor = !user?.is_team_member || ["supervisor", "coordenador", "gerente", "admin_company", "bko"].includes(user?.team_role);
  const [vendedorFiltro, setVendedorFiltro] = useState("");
  const [listaVendedores, setListaVendedores] = useState([]);

  // Filtros de Gestor/Coordenador/Supervisor — por NOME CRU da planilha
  // (gerente_nome/coordenador_nome/supervisor_nome), igual o de Vendedor:
  // não depende de ninguém ter virado conta no sistema. Mesma visibilidade
  // do filtro de Vendedor (só quem pode ver a equipe toda usa isso).
  const CAMPOS_LIDERANCA = { gerente: "gerente_nome", coordenador: "coordenador_nome", supervisor: "supervisor_nome" };
  const LABEL_PAPEL_LIDER = { gerente: "Gestor", coordenador: "Coordenador", supervisor: "Supervisor" };
  const [filtrosLideranca, setFiltrosLideranca] = useState({ gerente: "", coordenador: "", supervisor: "" });
  const [listasLideranca, setListasLideranca] = useState({ gerente: [], coordenador: [], supervisor: [] });

  useEffect(() => {
    if (!podeFiltrarVendedor) return;
    (async () => {
      try {
        const { data } = await api.get("/company/pipeline/historico/vendedores");
        setListaVendedores(data.vendedores || []);
      } catch (e) { /* silencioso — filtro só some se der erro */ }
    })();
  }, [podeFiltrarVendedor]);

  useEffect(() => {
    if (!podeFiltrarVendedor) return;
    (async () => {
      const resultados = {};
      for (const [papel, campo] of Object.entries(CAMPOS_LIDERANCA)) {
        try {
          const { data } = await api.get(`/company/pipeline/historico/nomes-lideranca?campo=${campo}`);
          resultados[papel] = data.nomes || [];
        } catch (e) { resultados[papel] = []; }
      }
      setListasLideranca(resultados);
    })();
  }, [podeFiltrarVendedor]); // eslint-disable-line react-hooks/exhaustive-deps

  const buscar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dataDe) params.set("data_de", dataDe);
      if (dataAte) params.set("data_ate", dataAte);
      if (busca.trim()) params.set("busca", busca.trim());
      if (colunasFiltro.size) params.set("colunas", [...colunasFiltro].join(","));
      params.set("campo_data", campoData);
      if (vendedorFiltro) params.set("vendedor", vendedorFiltro);
      if (filtrosLideranca.gerente) params.set("gerente", filtrosLideranca.gerente);
      if (filtrosLideranca.coordenador) params.set("coordenador", filtrosLideranca.coordenador);
      if (filtrosLideranca.supervisor) params.set("supervisor", filtrosLideranca.supervisor);
      const { data } = await api.get(`/company/pipeline/historico?${params.toString()}`);
      setLeads(data.leads);
      setResumo(data.resumo);
      setColunasAtivas(data.colunas_ativas || []);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao buscar histórico."));
    } finally { setLoading(false); }
  }, [dataDe, dataAte, busca, colunasFiltro, campoData, vendedorFiltro, filtrosLideranca]);

  useEffect(() => { buscar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Deixa o componente pai (CompanyPipelinePage) chamar historicoRef.current.reload()
  // depois de salvar uma edição no modal, sem precisar levantar todo o estado
  // do histórico pra cima — a tabela só recarrega quando precisa.
  React.useImperativeHandle(ref, () => ({ reload: buscar }), [buscar]);

  const toggleColuna = (id) => {
    setColunasFiltro(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const limparFiltros = () => {
    setDataDe(""); setDataAte(""); setBusca(""); setColunasFiltro(new Set()); setCampoData("updated_at"); setVendedorFiltro(""); setFiltrosLideranca({ gerente: "", coordenador: "", supervisor: "" });
  };

  return (
    <div>
      <div style={{ background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 14, padding: 18, marginBottom: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 8 }}>
          <label style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>De
            <input type="date" value={dataDe} onChange={e => setDataDe(e.target.value)}
              style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 13 }} />
          </label>
          <label style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>Até
            <input type="date" value={dataAte} onChange={e => setDataAte(e.target.value)}
              style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 13 }} />
          </label>
          <label style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>Buscar cliente (nome, CNPJ, telefone ou número do pedido)
            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Ex: João da Silva, 12.345.678/0001-90, #100003..."
              style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 13 }} />
          </label>
          {podeFiltrarVendedor && (
            <label style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>Vendedor
              <select value={vendedorFiltro} onChange={e => setVendedorFiltro(e.target.value)}
                style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 13 }}>
                <option value="">Todos</option>
                {listaVendedores.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
          )}
          {podeFiltrarVendedor && ["gerente", "coordenador", "supervisor"].map(papel => (
            <label key={papel} style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>{LABEL_PAPEL_LIDER[papel]}
              <select
                value={filtrosLideranca[papel]}
                onChange={e => setFiltrosLideranca(prev => ({ ...prev, [papel]: e.target.value }))}
                style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 13 }}>
                <option value="">Todos</option>
                {(listasLideranca[papel] || []).map(nome => <option key={nome} value={nome}>{nome}</option>)}
              </select>
            </label>
          ))}
        </div>

        {/* O "De"/"Até" acima filtra por um destes dois campos — deixar
            escolhido explicitamente evita o susto de "escolhi a data certa
            e não veio nada", que era exatamente por estar filtrando por
            updated_at (toque do sync) num período em que só a instalação
            de verdade caiu, não nenhum toque do card. */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>Filtrar "De"/"Até" por:</span>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[
              { id: "updated_at", label: "Atualizado" },
              { id: "dt_instalacao", label: "Instalado (DT Instalação)" },
            ].map(op => (
              <button key={op.id} onClick={() => setCampoData(op.id)}
                style={{
                  padding: "5px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer",
                  border: campoData === op.id ? "1px solid #a855f7" : "1px solid hsl(var(--border))",
                  background: campoData === op.id ? "#a855f722" : "hsl(var(--background))",
                  color: campoData === op.id ? "#a855f7" : "hsl(var(--muted-foreground))",
                }}>
                {op.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
          {COLS.map(c => (
            <button key={c.id} onClick={() => toggleColuna(c.id)}
              style={{
                fontSize: 11.5, padding: "5px 11px", borderRadius: 999, cursor: "pointer",
                border: `1px solid ${colunasFiltro.has(c.id) ? c.color : "hsl(var(--border))"}`,
                background: colunasFiltro.has(c.id) ? `${c.color}22` : "transparent",
                color: colunasFiltro.has(c.id) ? c.color : "hsl(var(--muted-foreground))",
                fontWeight: colunasFiltro.has(c.id) ? 700 : 500,
              }}>
              {c.label}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={buscar} disabled={loading}
            style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#a855f7", color: "#fff", fontSize: 13, fontWeight: 700, cursor: loading ? "default" : "pointer" }}>
            {loading ? "Buscando..." : "Filtrar"}
          </button>
          <button onClick={limparFiltros}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid hsl(var(--border))", background: "transparent", color: "hsl(var(--muted-foreground))", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Limpar filtros
          </button>
        </div>
      </div>

      {resumo && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>
          <div style={{ padding: "10px 16px", borderRadius: 10, background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))" }}>
            <div style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))" }}>Total encontrado</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "hsl(var(--foreground))" }}>{resumo.total}</div>
          </div>
          <div style={{ padding: "10px 16px", borderRadius: 10, background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))" }}>
            <div style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))" }}>Valor somado</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#22c55e" }}>{fmtBRL(resumo.valor_total)}</div>
          </div>
          {COLS.filter(c => resumo[c.id] > 0).map(c => (
            <div key={c.id} style={{ padding: "10px 16px", borderRadius: 10, background: "hsl(var(--secondary))", border: `1px solid ${c.color}44` }}>
              <div style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))" }}>{c.label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: c.color }}>{resumo[c.id]}</div>
            </div>
          ))}
        </div>
      )}

      {/* gridTemplateColumns ganha uma coluna de 130px pra cada campo mapeado
          na Sincronização de Base, além das fixas — por isso o wrapper tem
          overflow-x: auto, senão com muitos campos mapeados a tabela
          estoura a tela. minWidth garante que o grid não espreme as colunas
          fixas pra caber, forçando o scroll em vez disso. */}
      <div style={{ border: "1px solid hsl(var(--border))", borderRadius: 12, overflow: "auto" }}>
        <div style={{ minWidth: 860 + colunasAtivas.length * 130 }}>
          <div style={{ display: "grid", gridTemplateColumns: `70px 1.4fr 1fr 1fr 0.9fr 1fr 0.9fr 1fr ${colunasAtivas.map(() => "130px").join(" ")} 36px`, gap: 8, padding: "10px 14px", background: "hsl(var(--secondary))", fontSize: 11, fontWeight: 700, color: "hsl(var(--muted-foreground))", textTransform: "uppercase" }}>
            <div>Nº</div><div>Cliente</div><div>Vendedor</div><div>Supervisor</div><div>Status</div><div>Plano</div><div>Valor</div><div>Atualizado</div>
            {colunasAtivas.map(c => <div key={c.chave} title={c.label} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.label}</div>)}
            <div></div>
          </div>
          {loading ? (
            <div style={{ padding: 30, textAlign: "center" }}><Loader2 className="animate-spin" size={18} color="hsl(var(--muted-foreground))" /></div>
          ) : leads.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", fontSize: 13, color: "hsl(var(--muted-foreground))" }}>Nenhuma venda encontrada com esses filtros.</div>
          ) : (
            leads.map(l => {
              const colInfo = COLS.find(c => c.id === l.col);
              return (
                <div key={l.id} style={{ display: "grid", gridTemplateColumns: `70px 1.4fr 1fr 1fr 0.9fr 1fr 0.9fr 1fr ${colunasAtivas.map(() => "130px").join(" ")} 36px`, gap: 8, padding: "10px 14px", borderTop: "1px solid hsl(var(--border))", fontSize: 12.5, color: "hsl(var(--foreground))", alignItems: "center" }}>
                  <div style={{ color: "#a855f7", fontWeight: 700 }}>{l.numero ? `#${l.numero}` : "—"}</div>
                  <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.name || "—"}</div>
                  <div style={{ color: "hsl(var(--muted-foreground))", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.vendedor_nome || "—"}</div>
                  <div style={{ color: "hsl(var(--muted-foreground))", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.supervisor_nome || "—"}</div>
                  <div>
                    <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: `${colInfo?.color}22`, color: colInfo?.color }}>
                      {colInfo?.label || l.col}
                    </span>
                  </div>
                  <div style={{ color: "hsl(var(--muted-foreground))", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.plano_negociado || "—"}</div>
                  <div style={{ fontWeight: 700, color: l.value > 0 ? "#22c55e" : "hsl(var(--muted-foreground))" }}>{l.value > 0 ? fmtBRL(l.value) : "—"}</div>
                  <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>{fmtData(l.updated_at)}</div>
                  {colunasAtivas.map(c => (
                    <div key={c.chave} title={l.campos_planilha?.[c.chave] || ""} style={{ color: "hsl(var(--muted-foreground))", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {l.campos_planilha?.[c.chave] || "—"}
                    </div>
                  ))}
                  <button onClick={() => onEdit(l)} title="Editar / ver histórico"
                    style={{ background: "none", border: "none", cursor: "pointer", color: "hsl(var(--muted-foreground))", padding: 4 }}>
                    <Pencil size={14} />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
});


// ═══════════════════ Indicadores (dashboard de % pra reunião/sócio) ═══════════════════
// Pedido explícito: mostrar % de Vivo Total x Fixa x Móvel e % por status
// (cancelado, para correção, instalado...) — visão de cima, não lista crua.
function IndicadoresTab() {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const { data } = await api.get("/company/pipeline/indicadores");
        if (ativo) setDados(data);
      } catch (e) {
        toast.error(getErrorMessage(e, "Erro ao carregar os indicadores."));
      } finally {
        if (ativo) setLoading(false);
      }
    })();
    return () => { ativo = false; };
  }, []);

  const CORES_STATUS = {
    perdido_bko: "#ef4444", para_correcao: "#f97316", concluido: "#22c55e",
    instalado: "#0ea5e9", ativo: "#14b8a6", enviado_bko: "#a855f7",
    lead: "#94a3b8", contato: "#94a3b8", proposta: "#94a3b8", fechado: "#94a3b8", perdido: "#ef4444",
  };
  const CORES_TIPO = ["#a855f7", "#0ea5e9", "#22c55e", "#f97316", "#eab308", "#ec4899", "#94a3b8"];

  const Barra = ({ label, quantidade, percentual, cor }) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 13, marginBottom: 6 }}>
        <span style={{ color: "hsl(var(--foreground))", fontWeight: 600 }}>{label}</span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          <strong style={{ color: "hsl(var(--foreground))", fontWeight: 700 }}>{percentual}%</strong>
          <span style={{ color: "hsl(var(--muted-foreground))", marginLeft: 5 }}>({quantidade})</span>
        </span>
      </div>
      <div style={{ height: 10, borderRadius: 999, background: "hsl(var(--secondary))", overflow: "hidden", boxShadow: "inset 0 1px 2px rgba(0,0,0,0.15)" }}>
        <div style={{
          width: `${percentual}%`, height: "100%", borderRadius: 999, transition: "width .4s ease",
          background: `linear-gradient(90deg, ${cor}cc, ${cor})`,
        }} />
      </div>
    </div>
  );

  // Card com friso colorido no topo + selo de ícone — visual de "KPI card"
  // de dashboard corporativo, em vez de caixa neutra com borda fina.
  const Painel = ({ icone: Icone, titulo, corFriso, children }) => (
    <div style={{
      border: "1px solid hsl(var(--border))", borderRadius: 14, background: "hsl(var(--card, var(--background)))",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)", overflow: "hidden",
    }}>
      <div style={{ height: 3, background: `linear-gradient(90deg, ${corFriso}, ${corFriso}55)` }} />
      <div style={{ padding: "18px 20px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 18 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: `${corFriso}1a`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icone size={15} color={corFriso} />
          </div>
          <span style={{ fontSize: 14, fontWeight: 800, color: "hsl(var(--foreground))", letterSpacing: "-.01em" }}>{titulo}</span>
        </div>
        {children}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ padding: 40, display: "flex", justifyContent: "center" }}>
        <Loader2 className="animate-spin" size={22} color="hsl(var(--muted-foreground))" />
      </div>
    );
  }

  if (!dados || dados.total === 0) {
    return (
      <div style={{ padding: 30, textAlign: "center", fontSize: 13, color: "hsl(var(--muted-foreground))", border: "1px solid hsl(var(--border))", borderRadius: 12 }}>
        Nenhum card no pipeline ainda pra calcular indicador nenhum.
      </div>
    );
  }

  const acha = (arr, chave, valor) => (arr.find(x => x[chave] === valor) || {}).percentual || 0;
  const pctCancelado = acha(dados.por_status, "status", "perdido_bko");
  const pctInstalado = acha(dados.por_status, "status", "instalado") + acha(dados.por_status, "status", "ativo");
  const pctCorrecao = acha(dados.por_status, "status", "para_correcao");

  // Faixa de KPIs no topo, os 4 números que um sócio/gestor quer ver de
  // cara antes de entrar no detalhe das barras — total, instalado,
  // cancelado, em correção.
  const kpis = [
    { label: "Total de cards", valor: dados.total, sufixo: "", cor: "#a855f7" },
    { label: "Instalado / Ativo", valor: pctInstalado.toFixed(1), sufixo: "%", cor: "#0ea5e9" },
    { label: "Cancelado", valor: pctCancelado.toFixed(1), sufixo: "%", cor: "#ef4444" },
    { label: "Em correção", valor: pctCorrecao.toFixed(1), sufixo: "%", cor: "#f97316" },
  ];

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 22 }}>
        {kpis.map(k => (
          <div key={k.label} style={{
            border: "1px solid hsl(var(--border))", borderRadius: 14, padding: "16px 18px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)",
            borderLeft: `3px solid ${k.cor}`,
          }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: ".03em", marginBottom: 6 }}>
              {k.label}
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: "hsl(var(--foreground))", letterSpacing: "-.02em" }}>
              {k.valor}{k.sufixo}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
        <Painel icone={TrendingUp} titulo="Por tipo de serviço" corFriso="#a855f7">
          {dados.por_tipo_servico.map((t, i) => (
            <Barra key={t.tipo} label={t.tipo} quantidade={t.quantidade} percentual={t.percentual} cor={CORES_TIPO[i % CORES_TIPO.length]} />
          ))}
        </Painel>

        <Painel icone={BarChart3} titulo="Por status" corFriso="#0ea5e9">
          {dados.por_status.map(s => (
            <Barra key={s.status} label={s.label} quantidade={s.quantidade} percentual={s.percentual} cor={CORES_STATUS[s.status] || "#94a3b8"} />
          ))}
        </Painel>
      </div>

      <RankingEquipePainel />
    </div>
  );
}

// Compara a performance dos subordinados DIRETOS de quem está logado —
// vendas (cards criados), instalado (qtd) e instalado (R$), em 3 painéis
// lado a lado (escalas bem diferentes entre si, então cada um tem seu
// próprio eixo — juntar tudo num gráfico só esmagaria a barra de "vendas"
// perto da de "R$"), nos últimos N dias. Some sozinho se não tiver
// ninguém reportando (ex: vendedor, ou líder sem equipe ainda).
function RankingEquipePainel() {
  const [dias, setDias] = useState(30);
  const [loading, setLoading] = useState(true);
  const [ranking, setRanking] = useState(null);

  useEffect(() => {
    let ativo = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/company/pipeline/ranking-equipe?dias=${dias}`);
        if (ativo) setRanking(data.ranking || []);
      } catch (e) {
        if (ativo) setRanking([]);
      } finally { if (ativo) setLoading(false); }
    })();
    return () => { ativo = false; };
  }, [dias]);

  if (!loading && (!ranking || ranking.length === 0)) return null;

  const abreviaNome = n => n.length > 13 ? n.slice(0, 12) + "…" : n;
  const fmtR$ = v => `R$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const MiniRanking = ({ titulo, campo, cor, formata }) => {
    const ordenado = [...(ranking || [])].sort((a, b) => b[campo] - a[campo]);
    const dataChart = ordenado.map(r => ({ nome: abreviaNome(r.nome), nomeCompleto: r.nome, valor: r[campo] }));
    return (
      <div style={{ border: "1px solid hsl(var(--border))", borderRadius: 14, padding: "14px 12px 8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)", flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 8, paddingLeft: 4 }}>{titulo}</div>
        <ResponsiveContainer width="100%" height={Math.max(160, dataChart.length * 34)}>
          <BarChart data={dataChart} layout="vertical" margin={{ left: 4, right: 20 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="nome" width={90} tick={{ fontSize: 11 }} />
            <Tooltip
              formatter={(val) => [formata ? formata(val) : val, titulo]}
              labelFormatter={(_, payload) => payload?.[0]?.payload?.nomeCompleto || ""}
            />
            <Bar dataKey="valor" fill={cor} radius={[0, 4, 4, 0]} label={{ position: "right", fontSize: 10.5, formatter: formata }} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 800, display: "flex", alignItems: "center", gap: 8 }}>
          <BarChart3 size={16} color="#a855f7" /> Desempenho da equipe
        </div>
        <select value={dias} onChange={e => setDias(Number(e.target.value))}
          style={{ fontSize: 12.5, padding: "6px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))" }}>
          <option value={7}>Últimos 7 dias</option>
          <option value={30}>Últimos 30 dias</option>
          <option value={90}>Últimos 90 dias</option>
        </select>
      </div>

      {loading ? (
        <div style={{ padding: 30, display: "flex", justifyContent: "center", border: "1px solid hsl(var(--border))", borderRadius: 14 }}>
          <Loader2 className="animate-spin" size={20} color="hsl(var(--muted-foreground))" />
        </div>
      ) : (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          <MiniRanking titulo="Vendas (cards criados)" campo="vendas" cor="#a855f7" />
          <MiniRanking titulo="Instalado (quantidade)" campo="instalado_qtd" cor="#0ea5e9" />
          <MiniRanking titulo="Instalado (R$)" campo="instalado_valor" cor="#22c55e" formata={fmtR$} />
        </div>
      )}
    </div>
  );
}


// ─── Aba Boletos (cobrança) ───────────────────────────────────
// Fluxo: 1) importa o mailing de cobrança (nome, cpf, telefone, email),
// 2) sobe o zip com os PDFs (nomeados "{cpf}.pdf"), 3) dispara — o boleto
// só é apagado do R2 depois de confirmar que o WhatsApp entregou.
function BoletosTab() {
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // ── Import por arquivo com mapeamento de colunas (igual ao Mailing do
  //    ProspectPage): sobe CSV/XLSX, adivinha as colunas pelo nome/conteúdo
  //    e deixa o usuário conferir antes de importar. ──
  const [step, setStep] = useState("list"); // "list" | "map"
  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [colName, setColName]   = useState("");
  const [colCpf, setColCpf]     = useState("");
  const [colPhone, setColPhone] = useState("");
  const [colEmail, setColEmail] = useState("");
  const [colAddress, setColAddress] = useState("");
  const fileRef = useRef(null);

  const [zipFile, setZipFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);

  // ── Disparo espaçado (08h-18h, 8-15min entre envios) ──
  const [schedule, setSchedule] = useState(null); // status vindo do backend
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [openingMsg, setOpeningMsg] = useState(
    "Oi {nome}! Passando aqui pra te enviar o boleto da sua fatura em aberto. Qualquer dúvida, é só chamar 🙂"
  );
  const [dailyLimit, setDailyLimit] = useState(50);

  const fetchSchedule = useCallback(async () => {
    try {
      const { data } = await api.get("/boletos/mailing/schedule/status");
      setSchedule(data);
      if (data.message_template) setOpeningMsg(data.message_template);
      if (data.daily_limit) setDailyLimit(data.daily_limit);
    } catch { /* silencioso — não é crítico pra tela funcionar */ }
  }, []);

  useEffect(() => {
    fetchSchedule();
    const id = setInterval(fetchSchedule, 20000); // atualiza sozinho a cada 20s
    return () => clearInterval(id);
  }, [fetchSchedule]);

  const handleStartSchedule = async () => {
    setScheduleLoading(true);
    try {
      const { data } = await api.post("/boletos/mailing/schedule/start", {
        message_template: openingMsg, max_leads: dailyLimit,
      });
      toast.success(`Disparo espaçado ligado — ${data.pending} contato(s) na fila (08h-18h).`);
      await fetchSchedule();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao ligar o disparo espaçado."));
    } finally { setScheduleLoading(false); }
  };

  const handleStopSchedule = async () => {
    setScheduleLoading(true);
    try {
      await api.post("/boletos/mailing/schedule/stop");
      toast.success("Disparo espaçado desligado.");
      await fetchSchedule();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao desligar o disparo espaçado."));
    } finally { setScheduleLoading(false); }
  };

  // Parser de CSV que respeita aspas — mesmo motivo do MailingTab: campos
  // com vírgula dentro de aspas (endereço, nome de empresa) não podem
  // quebrar em duas colunas e deslocar o resto da linha.
  const parseCsvText = (text) => {
    const delimiter = (text.slice(0, 2000).match(/;/g) || []).length >
                       (text.slice(0, 2000).match(/,/g) || []).length ? ";" : ",";
    const rows = [];
    let row = [], field = "", inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') { field += '"'; i++; }
          else inQuotes = false;
        } else field += c;
      } else if (c === '"') {
        inQuotes = true;
      } else if (c === delimiter) {
        row.push(field); field = "";
      } else if (c === "\n" || c === "\r") {
        if (c === "\r" && text[i + 1] === "\n") i++;
        row.push(field); field = "";
        if (row.length > 1 || row[0] !== "") rows.push(row);
        row = [];
      } else field += c;
    }
    if (field !== "" || row.length) { row.push(field); rows.push(row); }
    return rows;
  };

  // Lê o CSV/XLSX no navegador, sem enviar o arquivo pro servidor
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const ext = file.name.split(".").pop().toLowerCase();
    let rows = [];

    if (ext === "csv") {
      const text = await file.text();
      rows = parseCsvText(text);
    } else if (ext === "xlsx" || ext === "xls") {
      try {
        const XLSX = await import("xlsx");
        const ab = await file.arrayBuffer();
        const wb = XLSX.read(ab, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      } catch {
        toast.error("Erro ao ler planilha. Tente salvar como CSV e importar novamente.");
        return;
      }
    } else {
      toast.error("Formato não suportado. Use CSV, XLS ou XLSX.");
      return;
    }

    if (rows.length < 2) { toast.error("Planilha vazia ou sem dados."); return; }

    const headers = rows[0].map(h => String(h).trim());
    setRawHeaders(headers);
    setRawRows(rows.slice(1));

    // Tenta adivinhar as colunas automaticamente pelo nome do cabeçalho
    const guessName  = headers.findIndex(h => /nome|name|cliente/i.test(h));
    const guessCpf   = headers.findIndex(h => /cpf/i.test(h));
    const guessEmail = headers.findIndex(h => /e-?mail/i.test(h));
    const guessAddress = headers.findIndex(h => /endere[çc]o|address|rua|logradouro/i.test(h));

    // Telefone: prioriza cabeçalhos claros; se não achar, confirma pelo
    // conteúdo (parece telefone em boa parte da amostra)
    const sample = rows.slice(1, 21);
    const looksLikePhone = (val) => {
      const digits = String(val ?? "").replace(/\D/g, "");
      return digits.length >= 8 && digits.length <= 13;
    };
    const scoreColumn = (i) => sample.filter(r => looksLikePhone(r[i])).length;
    let guessPhone = headers.findIndex(h => /tel|fone|celular|whatsapp|phone|contato/i.test(h));
    if (guessPhone < 0 && sample.length) {
      const scored = headers
        .map((_, i) => ({ i, hits: scoreColumn(i) }))
        .sort((a, b) => b.hits - a.hits);
      if (scored.length && scored[0].hits >= Math.ceil(sample.length * 0.5)) {
        guessPhone = scored[0].i;
      }
    }

    setColName(guessName   >= 0 ? String(guessName)   : "");
    setColCpf(guessCpf     >= 0 ? String(guessCpf)     : "");
    setColPhone(guessPhone >= 0 ? String(guessPhone)   : "");
    setColEmail(guessEmail >= 0 ? String(guessEmail)   : "");
    setColAddress(guessAddress >= 0 ? String(guessAddress) : "");

    setStep("map");
  };

  const handleImport = async () => {
    if (colCpf === "" || colPhone === "") {
      toast.error("Selecione ao menos a coluna de CPF e a de telefone.");
      return;
    }
    const nameIdx  = colName  !== "" ? parseInt(colName)  : -1;
    const cpfIdx   = parseInt(colCpf);
    const phoneIdx = parseInt(colPhone);
    const emailIdx = colEmail !== "" ? parseInt(colEmail) : -1;
    const addressIdx = colAddress !== "" ? parseInt(colAddress) : -1;

    const contacts = rawRows
      .map(row => ({
        name: nameIdx >= 0 ? String(row[nameIdx] || "").trim() : "",
        cpf: String(row[cpfIdx] || "").trim(),
        phone: String(row[phoneIdx] || "").trim(),
        email: emailIdx >= 0 ? String(row[emailIdx] || "").trim() : "",
        address: addressIdx >= 0 ? String(row[addressIdx] || "").trim() : "",
      }))
      .filter(c => c.cpf && c.phone);

    if (contacts.length === 0) { toast.error("Nenhum contato válido encontrado (confira CPF e telefone)."); return; }

    setImporting(true);
    setImportResult(null);
    try {
      const { data } = await api.post("/boletos/mailing/import", { contacts });
      setImportResult(data);
      toast.success(
        data.replaced > 0
          ? `Lista substituída: ${data.replaced} contato(s) antigo(s) removido(s), ${data.imported} novo(s) importado(s).`
          : `${data.imported} contato(s) importado(s).`
      );
      setStep("list");
      setRawHeaders([]); setRawRows([]);
      setColName(""); setColCpf(""); setColPhone(""); setColEmail(""); setColAddress("");
      fetchSchedule();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao importar mailing de boletos."));
    } finally { setImporting(false); }
  };

  const handleUploadZip = async () => {
    if (!zipFile) { toast.error("Selecione o arquivo .zip com os boletos."); return; }
    setUploading(true);
    setUploadResult(null);
    try {
      const form = new FormData();
      form.append("file", zipFile);
      const { data } = await api.post("/boletos/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(data);
      toast.success(`${data.enviados_r2} boleto(s) enviado(s) pro armazenamento.`);
      setZipFile(null);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao subir o zip de boletos."));
    } finally { setUploading(false); }
  };

  const handleRun = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const { data } = await api.post("/boletos/mailing/run", { max_leads: 50 });
      setRunResult(data);
      if (data.dispatched) toast.success(`Disparo iniciado para ${data.dispatched} cliente(s).`);
      else toast.info(data.message || "Nenhum contato pendente.");
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao disparar boletos."));
    } finally { setRunning(false); }
  };

  const boxStyle = {
    background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))",
    borderRadius: 12, padding: 18, marginBottom: 16,
  };
  const labelStyle = { fontSize: 14, fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 };
  const hintStyle = { fontSize: 12.5, color: "hsl(var(--muted-foreground))", marginBottom: 10 };
  const btnStyle = { display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "1px solid #a855f7", background: "rgba(168,85,247,0.1)", color: "#a855f7", fontSize: 13, fontWeight: 600, cursor: "pointer" };
  const selectStyle = { width: "100%", padding: "7px 8px", borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 12.5 };

  // ── Etapa de mapeamento de colunas (após escolher o arquivo) ──
  if (step === "map") {
    return (
      <div>
        <button onClick={() => setStep("list")} style={{ background: "none", border: "none", color: "hsl(var(--muted-foreground))", cursor: "pointer", fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
          <ArrowLeft size={14} /> Voltar
        </button>
        <div style={boxStyle}>
          <div style={labelStyle}>📋 Mapeamento de colunas</div>
          <div style={hintStyle}>
            Encontramos <strong style={{ color: "hsl(var(--foreground))" }}>{rawRows.length} contatos</strong> com {rawHeaders.length} colunas. Diga qual coluna é qual:
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 6 }}>🆔 Coluna de CPF <span style={{ color: "#ef4444" }}>*</span></label>
              <select value={colCpf} onChange={e => setColCpf(e.target.value)} style={selectStyle}>
                <option value="">Selecione...</option>
                {rawHeaders.map((h, i) => <option key={i} value={i}>{h || `Coluna ${i + 1}`}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 6 }}>📞 Coluna de Telefone <span style={{ color: "#ef4444" }}>*</span></label>
              <select value={colPhone} onChange={e => setColPhone(e.target.value)} style={selectStyle}>
                <option value="">Selecione...</option>
                {rawHeaders.map((h, i) => <option key={i} value={i}>{h || `Coluna ${i + 1}`}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 6 }}>👤 Coluna de Nome <span style={{ color: "hsl(var(--muted-foreground))" }}>(opcional)</span></label>
              <select value={colName} onChange={e => setColName(e.target.value)} style={selectStyle}>
                <option value="">Sem nome</option>
                {rawHeaders.map((h, i) => <option key={i} value={i}>{h || `Coluna ${i + 1}`}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 6 }}>✉️ Coluna de E-mail <span style={{ color: "hsl(var(--muted-foreground))" }}>(opcional)</span></label>
              <select value={colEmail} onChange={e => setColEmail(e.target.value)} style={selectStyle}>
                <option value="">Sem e-mail</option>
                {rawHeaders.map((h, i) => <option key={i} value={i}>{h || `Coluna ${i + 1}`}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 6 }}>📍 Coluna de Endereço <span style={{ color: "hsl(var(--muted-foreground))" }}>(opcional, usado no {"{endereco}"})</span></label>
              <select value={colAddress} onChange={e => setColAddress(e.target.value)} style={selectStyle}>
                <option value="">Sem endereço</option>
                {rawHeaders.map((h, i) => <option key={i} value={i}>{h || `Coluna ${i + 1}`}</option>)}
              </select>
            </div>
          </div>

          {/* Preview das primeiras 3 linhas */}
          {rawRows.slice(0, 3).length > 0 && (
            <div style={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 8, padding: "10px 12px", marginBottom: 16 }}>
              <p style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginBottom: 6 }}>Prévia (3 primeiros):</p>
              {rawRows.slice(0, 3).map((row, i) => (
                <div key={i} style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", marginBottom: 3 }}>
                  <span style={{ color: "#22c55e" }}>{colCpf !== "" ? (row[parseInt(colCpf)] || "—") : "—"}</span>
                  {colPhone !== "" && <span> · {row[parseInt(colPhone)] || "—"}</span>}
                  {colName !== "" && <span> — {row[parseInt(colName)] || "—"}</span>}
                </div>
              ))}
            </div>
          )}

          <button
            onClick={handleImport}
            disabled={importing || colCpf === "" || colPhone === ""}
            style={{ width: "100%", padding: "10px 0", borderRadius: 8, background: importing || colCpf === "" || colPhone === "" ? "hsl(var(--secondary))" : "#a855f7", color: "#fff", fontWeight: 700, fontSize: 14, border: "none", cursor: importing || colCpf === "" || colPhone === "" ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            {importing ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
            {importing ? "Importando..." : `Importar ${rawRows.length} contatos`}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={boxStyle}>
        <div style={labelStyle}><ClipboardList size={15} /> 1. Mailing de cobrança</div>
        <div style={hintStyle}>Suba a planilha (CSV, XLS ou XLSX) com os clientes que têm fatura em aberto — nome, CPF, telefone e e-mail. Na próxima tela você confere qual coluna é qual. <strong style={{ color: "hsl(var(--foreground))" }}>Importar uma planilha nova substitui a lista atual inteira</strong> (não acumula com a anterior).</div>
        <input type="file" ref={fileRef} accept=".csv,.xls,.xlsx" onChange={handleFile} style={{ display: "none" }} />
        <button type="button" onClick={() => fileRef.current?.click()} style={btnStyle}>
          <Upload size={14} /> Escolher planilha
        </button>
        {importResult && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>
            ✅ {importResult.imported} importado(s) · ⏭️ {importResult.skipped} ignorado(s) (duplicado/inválido)
            {importResult.replaced > 0 && <> · 🔄 {importResult.replaced} da lista anterior substituído(s)</>}
          </div>
        )}
      </div>

      <div style={boxStyle}>
        <div style={labelStyle}><FileText size={15} /> 2. Upload dos boletos (.zip)</div>
        <div style={hintStyle}>Cada PDF dentro do zip deve se chamar <code>{"{CPF}.pdf"}</code> (ex: <code>12345678900.pdf</code>).</div>
        <input type="file" accept=".zip" onChange={e => setZipFile(e.target.files?.[0] || null)}
          style={{ fontSize: 13, color: "hsl(var(--foreground))" }} />
        <div style={{ marginTop: 10 }}>
          <button onClick={handleUploadZip} disabled={uploading || !zipFile} style={{ ...btnStyle, opacity: (!zipFile && !uploading) ? 0.6 : 1 }}>
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Subir boletos
          </button>
        </div>
        {uploadResult && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: "hsl(var(--muted-foreground))", lineHeight: 1.6 }}>
            📦 {uploadResult.total_no_zip} no zip · ✅ {uploadResult.enviados_r2} enviado(s)
            {uploadResult.divergentes?.length > 0 && <> · ⚠️ {uploadResult.divergentes.length} com CPF divergente (nome do arquivo ≠ CPF dentro do PDF)</>}
            {uploadResult.sem_cliente_no_mailing?.length > 0 && <> · ❓ {uploadResult.sem_cliente_no_mailing.length} sem cliente correspondente no mailing</>}
            {uploadResult.com_erro?.length > 0 && <> · ❌ {uploadResult.com_erro.length} com erro</>}

            {/* Detalhe do motivo — sem isso o usuário só via a contagem, sem
                saber o que de fato deu errado em cada arquivo. */}
            {uploadResult.com_erro?.length > 0 && (
              <div style={{ marginTop: 6, padding: "8px 10px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 6 }}>
                {uploadResult.com_erro.map((e, i) => (
                  <div key={i} style={{ color: "#ef4444" }}>❌ <code>{e.nome_arquivo}</code> — {e.motivo}</div>
                ))}
              </div>
            )}
            {uploadResult.divergentes?.length > 0 && (
              <div style={{ marginTop: 6, padding: "8px 10px", background: "rgba(234,179,8,0.08)", border: "1px solid rgba(234,179,8,0.25)", borderRadius: 6 }}>
                {uploadResult.divergentes.map((d, i) => (
                  <div key={i}>⚠️ <code>{d.nome_arquivo}</code> — nome do arquivo diz CPF <code>{d.cpf_do_nome}</code>, mas o texto dentro do PDF tem CPF <code>{d.cpf_do_conteudo || "não encontrado"}</code></div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={boxStyle}>
        <div style={labelStyle}><Phone size={15} /> 3. Envio espaçado (08h-18h) <span style={{ fontSize: 11, fontWeight: 600, color: "#22c55e", background: "rgba(34,197,94,0.12)", padding: "2px 8px", borderRadius: 999 }}>recomendado</span></div>
        <div style={hintStyle}>
          Manda 1 boleto por vez, com intervalo aleatório de 8-15 min, só entre 08h e 18h — mesma proteção anti-bloqueio do mailing do ProspectPage. Primeiro vai a mensagem de abertura, depois o PDF.
        </div>

        <label style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", display: "block", marginBottom: 6 }}>
          💬 Mensagem de abertura — use <code>{"{nome}"}</code> e <code>{"{endereco}"}</code>
        </label>
        <textarea
          value={openingMsg} onChange={e => setOpeningMsg(e.target.value)}
          rows={3}
          disabled={schedule?.enabled}
          style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid hsl(var(--border))", background: schedule?.enabled ? "hsl(var(--secondary))" : "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 13, resize: "vertical", marginBottom: 12 }}
        />

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>Limite diário:</label>
          <input
            type="number" min={1} max={300} value={dailyLimit}
            disabled={schedule?.enabled}
            onChange={e => setDailyLimit(Math.max(1, Math.min(300, parseInt(e.target.value) || 1)))}
            style={{ width: 70, padding: "5px 8px", borderRadius: 6, border: "1px solid hsl(var(--border))", background: schedule?.enabled ? "hsl(var(--secondary))" : "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 12.5 }}
          />
        </div>

        {/* Status */}
        <div style={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 8, padding: "10px 12px", marginBottom: 14, fontSize: 12.5, color: "hsl(var(--muted-foreground))", lineHeight: 1.7 }}>
          {schedule?.enabled ? "🟢 Ativo" : "⚪ Desligado"}
          {" · "}{schedule?.sent_today ?? 0}/{schedule?.daily_limit ?? dailyLimit} enviados hoje
          {" · "}{schedule?.pending ?? 0} pendente(s) no mailing
          {schedule?.enabled && schedule?.next_send_at && (() => {
            const proximo = new Date(schedule.next_send_at);
            const hoje = new Date();
            const mesmoDia = proximo.toDateString() === hoje.toDateString();
            const hora = proximo.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
            const dataLabel = mesmoDia ? `hoje às ${hora}` : `${proximo.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} às ${hora}`;
            return <><br />⏱️ Próximo envio: {dataLabel} {!mesmoDia && "(fora do horário comercial agora — retoma amanhã)"}</>;
          })()}
        </div>

        {schedule?.enabled ? (
          <button onClick={handleStopSchedule} disabled={scheduleLoading}
            style={{ ...btnStyle, border: "1px solid #ef4444", background: "rgba(239,68,68,0.1)", color: "#ef4444" }}>
            {scheduleLoading ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />} Desligar envio espaçado
          </button>
        ) : (
          <button onClick={handleStartSchedule} disabled={scheduleLoading || !openingMsg.trim()} style={btnStyle}>
            {scheduleLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Ligar envio espaçado
          </button>
        )}
      </div>

      <div style={{ ...boxStyle, opacity: 0.85 }}>
        <div style={labelStyle}><Download size={15} /> Disparo rápido (não recomendado para lotes grandes)</div>
        <div style={hintStyle}>Manda tudo de uma vez, com pausa de só 8-18s entre cada. Use só pra lotes pequenos/teste — pra lotes grandes prefira o envio espaçado acima, que reduz o risco de bloqueio do número.</div>
        <button onClick={handleRun} disabled={running} style={btnStyle}>
          {running ? <Loader2 size={14} className="animate-spin" /> : <Phone size={14} />} Disparar boletos pendentes agora
        </button>
        {runResult && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>
            {runResult.dispatched
              ? `🚀 ${runResult.dispatched} disparo(s) em andamento — acompanhe o status na lista de contatos.`
              : (runResult.message || "Nenhum contato pendente.")}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Aba Sincronização de Base (mailing por Google Sheets → Pipeline) ─────
// Esqueleto v1: conectar planilha → mapear colunas → de-para de status →
// dashboard com status da última sincronização. O ciclo automático roda
// sozinho no backend (a cada poucos minutos); aqui é só configuração +
// "sincronizar agora" pra não precisar esperar o ciclo pra ver o resultado.

// Ícones oficiais (base64 inline — 64x64 — evita depender de asset externo
// ou de import de arquivo estático só pra isso).
const ICON_GOOGLE_SHEETS = "data:image/png;base64,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";
const ICON_EXCEL = "data:image/png;base64,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";

function SincronizacaoBaseTab() {
  const { user } = useAuth();
  const isAdminCompany = !user?.team_owner_id || user?.team_role === "admin_company";
  const [loading, setLoading] = useState(true);
  const [mailing, setMailing] = useState(null);
  const [serviceEmail, setServiceEmail] = useState("");
  const [provider, setProvider] = useState("google"); // "google" | "onedrive" — só usado na tela de conectar
  const [view, setView] = useState("connect"); // "connect" | "map" | "dashboard"

  // Etapa 1: conectar
  const [spreadsheetUrl, setSpreadsheetUrl] = useState("");
  const [aba, setAba] = useState("");
  const [linhaCabecalho, setLinhaCabecalho] = useState(1);
  const [connecting, setConnecting] = useState(false);

  // Etapa 2: mapeamento
  const [cabecalhos, setCabecalhos] = useState([]);
  const [camposMapeaveis, setCamposMapeaveis] = useState({});
  // Os 4 campos de CPF (vendedor/gestor/PDV-equipe/parceiro indireto) só
  // servem pra hierarquia bater por CPF em vez de nome — a maioria das
  // planilhas nem tem essas colunas, e eles inflam a tela de mapeamento
  // (12+ dropdowns) pra quem não precisa. Ficam escondidos atrás de um
  // checkbox, ligado automaticamente se algum já estiver mapeado (senão
  // salvar de novo "esconderia" um mapeamento que já existia).
  const CAMPOS_CPF_COLABORADOR = ["cpf_vendedor", "cpf_gestor", "cpf_pdv_equipe", "cpf_parceiro_indireto"];
  const [mostrarCamposCpf, setMostrarCamposCpf] = useState(false);
  const [columnMap, setColumnMap] = useState({});
  const [totalLinhas, setTotalLinhas] = useState(0);

  // Etapa 3: de-para de status
  const [statusValues, setStatusValues] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [statusSourceCampo, setStatusSourceCampo] = useState("status"); // "status" | "esteira_agendamento" — qual coluna decide a fase no Kanban
  const [colunasPipeline, setColunasPipeline] = useState({});
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [saving, setSaving] = useState(false);

  const [syncingNow, setSyncingNow] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Auditoria (linha da planilha x card no CRM)
  const [auditando, setAuditando] = useState(false);
  const [auditGrupoAberto, setAuditGrupoAberto] = useState(null); // "nunca_sincronizadas" | "card_apagado_depois" | "sem_chave" | null

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get("/company/pipeline/sheet-sync");
      setServiceEmail(data.service_account_email || "");
      setCamposMapeaveis(data.campos_mapeaveis || {});
      if (data.mailing) {
        setMailing(data.mailing);
        setProvider(data.mailing.provider || "google");
        setColumnMap(data.mailing.column_map || {});
        setStatusMap(data.mailing.status_map || {});
        setStatusSourceCampo(data.mailing.status_source || "status");
        setCabecalhos(data.mailing.cabecalhos || []);
        const temMapeamentoCompleto = data.mailing.column_map?.telefone && data.mailing.column_map?.status;
        setView(temMapeamentoCompleto ? "dashboard" : "map");
      } else {
        setView("connect");
      }
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao carregar a Sincronização de Base."));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const handleConnect = async () => {
    if (!spreadsheetUrl.trim()) {
      toast.error(provider === "onedrive" ? "Cole o link de compartilhamento do OneDrive/Excel Online." : "Cole o link da planilha do Google Sheets.");
      return;
    }
    setConnecting(true);
    try {
      const { data } = await api.post("/company/pipeline/sheet-sync/connect", {
        spreadsheet_url: spreadsheetUrl.trim(), aba: aba.trim(), provider, linha_cabecalho: linhaCabecalho,
      });
      setCabecalhos(data.cabecalhos);
      setCamposMapeaveis(data.campos_mapeaveis);
      setTotalLinhas(data.total_linhas);
      setColumnMap({});
      setStatusMap({});
      setStatusValues([]);
      setStatusSourceCampo("status");
      toast.success(`Planilha conectada — ${data.total_linhas} linha(s) encontrada(s). Agora mapeia as colunas.`);
      setView("map");
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao conectar a planilha."));
    } finally { setConnecting(false); }
  };

  // Reabrir o mapeamento de um mailing já conectado — os campos mapeáveis já
  // vieram do carregar() inicial (GET /sheet-sync), não precisa inventar uma
  // lista hardcoded aqui (isso já foi bug: essa lista tinha ficado desatualizada
  // e sobrescrevia os campos novos quando o usuário clicava "Editar mapeamento").
  // Antes de abrir a tela, relê a planilha ao vivo (recarregar-colunas) —
  // sem isso, o dropdown de colunas ficava preso na "foto" de quando a
  // planilha foi conectada, e se alguém renomeasse uma coluna depois (ex:
  // "CPF 2" virou "CPF"), o nome novo nem aparecia pra escolher.
  const [recarregandoColunas, setRecarregandoColunas] = useState(false);
  const handleEditarMapeamento = async () => {
    setRecarregandoColunas(true);
    try {
      const { data } = await api.post("/company/pipeline/sheet-sync/recarregar-colunas");
      setCabecalhos(data.cabecalhos);
    } catch (e) {
      toast.error(getErrorMessage(e, "Não consegui reler as colunas da planilha agora — mostrando a última lista salva."));
    } finally {
      setRecarregandoColunas(false);
      setView("map");
    }
  };

  const carregarValoresStatus = useCallback(async (cabecalhoStatus) => {
    if (!cabecalhoStatus) return;
    setLoadingStatus(true);
    try {
      const { data } = await api.post("/company/pipeline/sheet-sync/valores-status", { cabecalho_status: cabecalhoStatus });
      setStatusValues(data.valores);
      setColunasPipeline(data.colunas_pipeline);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao ler os valores de status da planilha."));
    } finally { setLoadingStatus(false); }
  }, []);

  useEffect(() => {
    if (view === "map" && columnMap[statusSourceCampo]) carregarValoresStatus(columnMap[statusSourceCampo]);
  }, [view, statusSourceCampo, columnMap, carregarValoresStatus]);

  useEffect(() => {
    if (view === "map" && CAMPOS_CPF_COLABORADOR.some(c => columnMap[c])) setMostrarCamposCpf(true);
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSalvarConfig = async () => {
    setSaving(true);
    try {
      await api.post("/company/pipeline/sheet-sync/config", {
        column_map: columnMap, status_map: statusMap, status_source: statusSourceCampo, sync_enabled: true,
      });
      toast.success("Configuração salva — a sincronização automática já está ligada.");
      await carregar();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao salvar a configuração."));
    } finally { setSaving(false); }
  };

  const handleSyncNow = async () => {
    setSyncingNow(true);
    try {
      const { data } = await api.post("/company/pipeline/sheet-sync/sync-now");
      toast.success(data.msg || "Sincronização disparada — os cards devem aparecer em alguns segundos.");
      setTimeout(carregar, 4000);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao sincronizar."));
    } finally {
      // Cooldown de alguns segundos antes de liberar o botão de novo — a
      // sync roda em background no servidor e pode levar mais tempo que a
      // resposta HTTP (que só confirma o disparo, não o término). Isso é
      // só pra desencorajar clique repetido; o backend também tem trava
      // contra sincronização concorrente do mesmo mailing.
      setTimeout(() => setSyncingNow(false), 8000);
    }
  };

  const [preenchendoCampos, setPreenchendoCampos] = useState(false);
  const handlePreencherCamposFaltando = async () => {
    setPreenchendoCampos(true);
    try {
      const { data } = await api.post("/company/pipeline/sheet-sync/preencher-campos-faltando");
      if (data.cards_atualizados > 0) {
        toast.success(`${data.cards_atualizados} card(s) completado(s). ${data.linhas_sem_card_correspondente ? `(${data.linhas_sem_card_correspondente} linha(s) da planilha não bateram com nenhum card.)` : ""}`);
      } else if (data.linhas_sem_card_correspondente > 0) {
        toast.error(`Nenhum card atualizado — ${data.linhas_sem_card_correspondente} linha(s) da planilha não bateram com nenhum card existente (pode ser mudança na chave de identificação da linha).`);
      } else {
        toast.success("Nenhum card com campo faltando pra completar — está tudo em dia.");
      }
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao preencher campos faltando."));
    } finally { setPreenchendoCampos(false); }
  };

  const [deduplicando, setDeduplicando] = useState(false);
  const handleDesduplicar = async () => {
    setDeduplicando(true);
    try {
      const { data: preview } = await api.post("/company/pipeline/sheet-sync/desduplicar", { confirmar: false });
      if (!preview.grupos_duplicados_encontrados) {
        toast.success("Nenhum card duplicado encontrado — o pipeline está limpo.");
        return;
      }
      const ok = window.confirm(
        `Encontrei ${preview.grupos_duplicados_encontrados} grupo(s) de card duplicado ` +
        `(${preview.cards_que_seriam_removidos} card(s) seriam removidos ao todo).\n\n` +
        `Nada se perde: checklist, nota de backoffice, observações e histórico de cada ` +
        `duplicado são fundidos no card que sobrar antes de apagar os outros.\n\n` +
        `Aplicar agora?`
      );
      if (!ok) return;
      const { data: resultado } = await api.post("/company/pipeline/sheet-sync/desduplicar", { confirmar: true });
      toast.success(`Pronto! ${resultado.cards_removidos} card(s) duplicado(s) fundido(s) em ${resultado.grupos_duplicados_encontrados} grupo(s).`);
      carregar();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao corrigir cards duplicados."));
    } finally {
      setDeduplicando(false);
    }
  };

  const handleAuditarAgora = async () => {
    setAuditando(true);
    try {
      const { data } = await api.post("/company/pipeline/sheet-sync/auditoria");
      setMailing(m => ({ ...m, last_audit: data.auditoria }));
      toast.success("Auditoria concluída.");
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao auditar a planilha."));
    } finally { setAuditando(false); }
  };

  const handleDesconectar = async () => {
    if (!window.confirm("Desconectar a planilha? Os cards já criados continuam no pipeline — só a sincronização automática para.")) return;
    setDisconnecting(true);
    try {
      await api.delete("/company/pipeline/sheet-sync");
      toast.success("Planilha desconectada.");
      setMailing(null);
      setSpreadsheetUrl(""); setAba("");
      setView("connect");
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao desconectar."));
    } finally { setDisconnecting(false); }
  };

  const copiarEmail = () => {
    navigator.clipboard.writeText(serviceEmail);
    toast.success("E-mail copiado.");
  };

  const boxStyle = { background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 12, padding: 18, marginBottom: 16 };
  const labelStyle = { fontSize: 14, fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 };
  const hintStyle = { fontSize: 12.5, color: "hsl(var(--muted-foreground))", marginBottom: 10 };
  const inputStyle = { width: "100%", padding: "9px 10px", borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 13.5 };
  const selectStyle = { width: "100%", padding: "7px 8px", borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 12.5 };
  const btnStyle = { display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "1px solid #a855f7", background: "rgba(168,85,247,0.1)", color: "#a855f7", fontSize: 13, fontWeight: 600, cursor: "pointer" };
  const btnGhostStyle = { display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "1px solid hsl(var(--border))", background: "none", color: "hsl(var(--muted-foreground))", fontSize: 13, fontWeight: 600, cursor: "pointer" };

  if (loading) {
    return <div style={{ padding: 40, display: "flex", justifyContent: "center" }}><Loader2 className="animate-spin" size={22} color="hsl(var(--muted-foreground))" /></div>;
  }

  // ── Etapa: conectar planilha ──
  if (view === "connect") {
    const btnProviderStyle = (ativo) => ({
      display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, cursor: "pointer",
      border: ativo ? "1.5px solid #a855f7" : "1px solid hsl(var(--border))",
      background: ativo ? "rgba(168,85,247,0.1)" : "hsl(var(--background))",
      color: ativo ? "#a855f7" : "hsl(var(--foreground))", fontSize: 13.5, fontWeight: 600, flex: 1,
    });
    return (
      <div>
        <div style={boxStyle}>
          <div style={labelStyle}><Link2 size={15} /> Conectar planilha</div>
          <div style={hintStyle}>
            Cole o link da planilha que os BKOs já usam pra tratar o mailing de vendas. O sistema vai ler
            ela periodicamente: linha nova de cliente vira card novo no pipeline, mudança na coluna de
            status move o card, e mudança na coluna de observações aparece no card sem mexer ele de lugar.
          </div>

          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <button type="button" onClick={() => setProvider("google")} style={btnProviderStyle(provider === "google")}>
              <img src={ICON_GOOGLE_SHEETS} alt="" width={18} height={18} style={{ display: "block" }} /> Google Sheets
            </button>
            <button type="button" onClick={() => setProvider("onedrive")} style={btnProviderStyle(provider === "onedrive")}>
              <img src={ICON_EXCEL} alt="" width={18} height={18} style={{ display: "block" }} /> OneDrive / Excel Online
            </button>
          </div>

          {provider === "google" && serviceEmail && (
            <div style={{ background: "hsl(var(--background))", border: "1px dashed hsl(var(--border))", borderRadius: 8, padding: "10px 12px", marginBottom: 14, fontSize: 12.5 }}>
              <strong style={{ color: "hsl(var(--foreground))" }}>Antes de colar o link:</strong> compartilhe a planilha
              (permissão de leitor já basta) com este e-mail:
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                <code style={{ background: "hsl(var(--secondary))", padding: "4px 8px", borderRadius: 6, fontSize: 12, color: "hsl(var(--foreground))", flex: 1, overflowWrap: "anywhere" }}>{serviceEmail}</code>
                <button onClick={copiarEmail} title="Copiar" style={{ background: "none", border: "1px solid hsl(var(--border))", borderRadius: 6, padding: 6, cursor: "pointer", color: "hsl(var(--muted-foreground))" }}>
                  <Copy size={13} />
                </button>
              </div>
            </div>
          )}

          {provider === "onedrive" && (
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 8, padding: "12px 14px", marginBottom: 14, fontSize: 12.5 }}>
              <AlertTriangle size={16} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <strong style={{ color: "#f59e0b" }}>Obrigatório:</strong> a planilha precisa estar compartilhada como{" "}
                <strong>"Qualquer pessoa com o link pode ver"</strong> (no Excel Online/OneDrive: Compartilhar → Copiar link,
                com essa opção marcada). Não precisa compartilhar com nenhum e-mail específico — mas se o link não for
                público, a sincronização <strong>não vai funcionar</strong>.
              </div>
            </div>
          )}

          <input
            value={spreadsheetUrl}
            onChange={e => setSpreadsheetUrl(e.target.value)}
            placeholder={provider === "onedrive" ? "https://empresa-my.sharepoint.com/:x:/... ou https://1drv.ms/x/..." : "https://docs.google.com/spreadsheets/d/..."}
            style={{ ...inputStyle, marginBottom: 10 }}
          />
          <input value={aba} onChange={e => setAba(e.target.value)} placeholder='Nome da aba (opcional — deixe vazio pra usar a primeira)' style={{ ...inputStyle, marginBottom: 10 }} />
          <label style={{ fontSize: 12, fontWeight: 600, color: "hsl(var(--foreground))", display: "block", marginBottom: 3 }}>
            Em qual linha estão os nomes das colunas?
          </label>
          <input
            type="number" min={1} max={20} value={linhaCabecalho}
            onChange={e => setLinhaCabecalho(Math.max(1, parseInt(e.target.value, 10) || 1))}
            style={{ ...inputStyle, marginBottom: 6, maxWidth: 100 }}
          />
          <div style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", marginBottom: 14 }}>
            Normalmente é 1 (primeira linha). Use 2 se a linha 1 tiver um título/nome da equipe acima do
            cabeçalho de verdade — tudo acima da linha escolhida é ignorado.
          </div>
          <button onClick={handleConnect} disabled={connecting} style={{ ...btnStyle, opacity: connecting ? 0.6 : 1 }}>
            {connecting ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} Conectar planilha
          </button>
        </div>
      </div>
    );
  }

  // ── Etapa: mapeamento de colunas + de-para de status ──
  if (view === "map") {
    const camposEntries = Object.entries(camposMapeaveis).filter(
      ([k]) => mostrarCamposCpf || !CAMPOS_CPF_COLABORADOR.includes(k)
    );
    const obrigatoriosFaltando = camposEntries.filter(([k, c]) => c.obrigatorio && !columnMap[k]);
    const CAMPOS_IDENTIFICADORES = ["identificador", "telefone", "cnpj"];
    const temIdentificador = CAMPOS_IDENTIFICADORES.some(c => columnMap[c]);
    const podeSalvar = obrigatoriosFaltando.length === 0 && temIdentificador;
    const btnProviderStyleGenerico = (ativo) => ({
      padding: "8px 14px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 600,
      border: ativo ? "1.5px solid #a855f7" : "1px solid hsl(var(--border))",
      background: ativo ? "rgba(168,85,247,0.1)" : "hsl(var(--background))",
      color: ativo ? "#a855f7" : "hsl(var(--foreground))",
    });

    return (
      <div>
        {mailing && (
          <button onClick={() => setView("dashboard")} style={{ background: "none", border: "none", color: "hsl(var(--muted-foreground))", cursor: "pointer", fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <ArrowLeft size={14} /> Voltar
          </button>
        )}
        <div style={boxStyle}>
          <div style={labelStyle}><Table2 size={15} /> Mapeamento de colunas</div>
          <div style={hintStyle}>
            {totalLinhas > 0 && <>Encontramos <strong style={{ color: "hsl(var(--foreground))" }}>{totalLinhas} linha(s)</strong> na planilha. </>}
            Diga qual coluna de lá é qual campo do pipeline:
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "hsl(var(--muted-foreground))", marginBottom: 14, cursor: "pointer", userSelect: "none" }}>
            <input type="checkbox" checked={mostrarCamposCpf} onChange={e => setMostrarCamposCpf(e.target.checked)} style={{ width: 15, height: 15 }} />
            Incluir CPF de colaborador? (mostra os campos de CPF do vendedor/gestor/coordenador/supervisor — deixa mais preciso a hierarquia bater por CPF em vez de nome, mas a maioria das planilhas não tem essas colunas)
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
            {camposEntries.map(([campo, info]) => (
              <div key={campo}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "hsl(var(--foreground))", display: "block", marginBottom: 3 }}>
                  {info.label}{info.obrigatorio && <span style={{ color: "#ef4444" }}> *</span>}
                </label>
                <select
                  value={columnMap[campo] || ""}
                  onChange={e => setColumnMap(prev => ({ ...prev, [campo]: e.target.value }))}
                  style={selectStyle}
                >
                  <option value="">— não usar —</option>
                  {cabecalhos.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>

        {(columnMap.status || columnMap.esteira_agendamento) && (
          <div style={boxStyle}>
            <div style={labelStyle}><ArrowRightLeft size={15} /> Qual coluna decide a fase no Kanban?</div>
            <div style={hintStyle}>
              Normalmente é o "Status/Tratativa" — mas em planilhas de pós-venda o que manda de verdade
              costuma ser a "Esteira de Agendamento" (ex: Pendente Agendamento, Instalado, Cancelado).
              Escolhe qual das duas colunas a sincronização deve olhar pra mover o card.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" disabled={!columnMap.status} onClick={() => setStatusSourceCampo("status")}
                style={{ ...btnProviderStyleGenerico(statusSourceCampo === "status"), opacity: columnMap.status ? 1 : 0.4, cursor: columnMap.status ? "pointer" : "not-allowed" }}>
                Status/Tratativa
              </button>
              <button type="button" disabled={!columnMap.esteira_agendamento} onClick={() => setStatusSourceCampo("esteira_agendamento")}
                style={{ ...btnProviderStyleGenerico(statusSourceCampo === "esteira_agendamento"), opacity: columnMap.esteira_agendamento ? 1 : 0.4, cursor: columnMap.esteira_agendamento ? "pointer" : "not-allowed" }}>
                Esteira de Agendamento
              </button>
            </div>
          </div>
        )}

        {columnMap[statusSourceCampo] && (
          <div style={boxStyle}>
            <div style={labelStyle}><ArrowRightLeft size={15} /> De-para de status → coluna do pipeline</div>
            <div style={hintStyle}>
              Pra cada valor que já aparece na coluna de {statusSourceCampo === "esteira_agendamento" ? "esteira" : "status"} da
              planilha, escolha em qual coluna do pipeline o card deve cair. Deixe em branco os que não devem
              mover o card (ex: um status intermediário tipo "Ligar novamente" — nesse caso o card só recebe a
              observação, sem mudar de fase).
            </div>
            {loadingStatus ? (
              <Loader2 size={16} className="animate-spin" />
            ) : statusValues.length === 0 ? (
              <div style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>Nenhum valor encontrado ainda nessa coluna.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {statusValues.map(valor => (
                  <div key={valor} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 13, flex: 1, color: "hsl(var(--foreground))", fontWeight: 600 }}>{valor}</span>
                    <ChevronDown size={13} style={{ color: "hsl(var(--muted-foreground))", transform: "rotate(-90deg)", flexShrink: 0 }} />
                    <select
                      value={statusMap[valor] || ""}
                      onChange={e => setStatusMap(prev => ({ ...prev, [valor]: e.target.value }))}
                      style={{ ...selectStyle, flex: 1 }}
                    >
                      <option value="">— não mover o card —</option>
                      {Object.entries(colunasPipeline).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <button onClick={handleSalvarConfig} disabled={!podeSalvar || saving} style={{ ...btnStyle, opacity: (!podeSalvar || saving) ? 0.5 : 1 }}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Salvar e ligar sincronização automática
        </button>
        {!podeSalvar && (
          <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", marginTop: 6 }}>
            {obrigatoriosFaltando.length > 0 && <>Falta mapear: {obrigatoriosFaltando.map(([, c]) => c.label).join(", ")}. </>}
            {!temIdentificador && "Mapeie pelo menos um identificador único de linha (Identificador, Telefone ou CNPJ/CPF) — sem isso o sistema não sabe diferenciar uma linha nova de uma já sincronizada."}
          </div>
        )}
      </div>
    );
  }

  // ── Dashboard (planilha conectada e mapeada) ──
  const ultimoResultado = mailing?.last_result;
  return (
    <div>
      <div style={boxStyle}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={labelStyle}><Table2 size={15} /> Planilha conectada {mailing?.provider === "onedrive" ? "(OneDrive/Excel Online)" : "(Google Sheets)"}</div>
            <div style={hintStyle}>
              {mailing?.aba ? `Aba "${mailing.aba}"` : "Primeira aba"}
              {mailing?.linha_cabecalho > 1 ? ` · cabeçalho na linha ${mailing.linha_cabecalho}` : ""} · sincronização automática a cada poucos minutos
            </div>
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#16a34a", background: "rgba(34,197,94,0.12)", padding: "3px 9px", borderRadius: 999, flexShrink: 0 }}>
            ● Ativa
          </span>
        </div>

        {mailing?.last_error ? (
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, padding: "10px 12px", marginTop: 12, fontSize: 12.5, color: "#ef4444" }}>
            <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
            <div><strong>Última sincronização falhou:</strong> {mailing.last_error}</div>
          </div>
        ) : ultimoResultado ? (
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 14 }}>
            {[
              ["Criados", ultimoResultado.criados, "#22c55e"],
              ["Movidos", ultimoResultado.movidos, "#0ea5e9"],
              ["Observações atualizadas", ultimoResultado.obs_atualizadas, "#f59e0b"],
              ["Sem mudança", ultimoResultado.sem_mudanca, "hsl(var(--muted-foreground))"],
            ].map(([label, val, color]) => (
              <div key={label}>
                <div style={{ fontSize: 20, fontWeight: 800, color }}>{val ?? 0}</div>
                <div style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>{label}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))", marginTop: 10 }}>Ainda não rodou nenhum ciclo — clica em "Sincronizar agora".</div>
        )}

        {mailing?.last_synced_at && (
          <div style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", marginTop: 10 }}>
            Última sincronização: {new Date(mailing.last_synced_at).toLocaleString("pt-BR")}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
          <button onClick={handleSyncNow} disabled={syncingNow} style={{ ...btnStyle, opacity: syncingNow ? 0.6 : 1 }}>
            {syncingNow ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Sincronizar agora
          </button>
          {isAdminCompany && (
            <button onClick={handlePreencherCamposFaltando} disabled={preenchendoCampos}
              title="Relê a planilha e completa CPF/nome/telefone/plano/valor que ficaram vazios em cards já existentes — sem sobrescrever o que já está preenchido."
              style={{ ...btnGhostStyle, opacity: preenchendoCampos ? 0.6 : 1 }}>
              {preenchendoCampos ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Preencher campos faltando
            </button>
          )}
          <button onClick={handleEditarMapeamento} disabled={recarregandoColunas} style={{ ...btnGhostStyle, opacity: recarregandoColunas ? 0.6 : 1 }}>
            {recarregandoColunas ? <Loader2 size={14} className="animate-spin" /> : <Pencil size={14} />} Editar mapeamento
          </button>
          {isAdminCompany && (
            <button onClick={handleDesduplicar} disabled={deduplicando} style={{ ...btnGhostStyle, opacity: deduplicando ? 0.6 : 1 }} title="Encontra cards duplicados (ex: de uma falha de sincronização) e funde num só, sem perder edições do backoffice. Restrito ao admin da empresa.">
            {deduplicando ? <Loader2 size={14} className="animate-spin" /> : <ArrowRightLeft size={14} />} Corrigir cards duplicados
          </button>
          )}
          <button onClick={handleDesconectar} disabled={disconnecting} style={{ ...btnGhostStyle, color: "#ef4444", borderColor: "rgba(239,68,68,0.3)" }}>
            {disconnecting ? <Loader2 size={14} className="animate-spin" /> : <Unplug size={14} />} Desconectar
          </button>
        </div>
      </div>

      <AuditoriaSheetSyncPainel
        auditoria={mailing?.last_audit}
        auditando={auditando}
        onAuditarAgora={handleAuditarAgora}
        grupoAberto={auditGrupoAberto}
        setGrupoAberto={setAuditGrupoAberto}
      />

      {isAdminCompany && <HierarquiaPlanilhaPainel />}
      {isAdminCompany && <DiagnosticoCpfPainel />}    </div>
  );
}

// Mini-dashboard: quais linhas da planilha não viraram card no CRM. Roda
// sozinho depois de cada ciclo automático (last_audit vem pronto do
// backend); o botão "Auditar agora" só força recalcular sem esperar.
function AuditoriaSheetSyncPainel({ auditoria, auditando, onAuditarAgora, grupoAberto, setGrupoAberto }) {
  const painelStyle = { background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 12, padding: 16, marginTop: 16 };
  const btnGhostStyle = { display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "1px solid hsl(var(--border))", background: "none", color: "hsl(var(--muted-foreground))", fontSize: 13, fontWeight: 600, cursor: "pointer" };

  if (!auditoria) {
    return (
      <div style={painelStyle}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Auditoria: planilha x cards no CRM</div>
          <button onClick={onAuditarAgora} disabled={auditando} style={{ ...btnGhostStyle, opacity: auditando ? 0.6 : 1 }}>
            {auditando ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Auditar agora
          </button>
        </div>
        <div style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))", marginTop: 8 }}>
          Ainda não rodou nenhuma auditoria — roda sozinha depois do próximo ciclo, ou clica em "Auditar agora".
        </div>
      </div>
    );
  }

  if (auditoria.erro) {
    return (
      <div style={painelStyle}>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", color: "#ef4444", fontSize: 12.5 }}>
          <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <div><strong>Auditoria falhou:</strong> {auditoria.erro}</div>
        </div>
      </div>
    );
  }

  const grupos = [
    { chave: "nunca_sincronizadas", label: "Nunca viraram card", cor: "#ef4444", explicacao: "Linha existe na planilha mas nunca gerou card no pipeline." },
    { chave: "card_apagado_depois", label: "Card apagado depois", cor: "#f59e0b", explicacao: "Chegou a criar o card, mas ele foi excluído do pipeline depois." },
    { chave: "sem_chave", label: "Sem telefone/CNPJ", cor: "hsl(var(--muted-foreground))", explicacao: "Linha sem telefone nem CNPJ/CPF — não dá pra rastrear nem virar card." },
  ];
  const totalProblemas = grupos.reduce((acc, g) => acc + (auditoria[g.chave]?.total || 0), 0);

  return (
    <div style={painelStyle}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Auditoria: planilha x cards no CRM</div>
          <div style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", marginTop: 2 }}>
            {auditoria.total_linhas_com_dado} linha(s) na planilha · última auditoria: {new Date(auditoria.auditado_em).toLocaleString("pt-BR")}
          </div>
        </div>
        <button onClick={onAuditarAgora} disabled={auditando} style={{ ...btnGhostStyle, opacity: auditando ? 0.6 : 1 }}>
          {auditando ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Auditar agora
        </button>
      </div>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 14 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#22c55e" }}>{auditoria.total_ok ?? 0}</div>
          <div style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>OK (viraram card)</div>
        </div>
        {grupos.map(g => (
          <div key={g.chave}>
            <div style={{ fontSize: 20, fontWeight: 800, color: g.cor }}>{auditoria[g.chave]?.total ?? 0}</div>
            <div style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>{g.label}</div>
          </div>
        ))}
      </div>

      {totalProblemas === 0 ? (
        <div style={{ fontSize: 12.5, color: "#22c55e", marginTop: 12 }}>Todas as linhas da planilha viraram card. Nenhuma pendência. ✓</div>
      ) : (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          {grupos.filter(g => (auditoria[g.chave]?.total || 0) > 0).map(g => {
            const aberto = grupoAberto === g.chave;
            const linhas = auditoria[g.chave]?.linhas || [];
            return (
              <div key={g.chave} style={{ border: "1px solid hsl(var(--border))", borderRadius: 9, overflow: "hidden" }}>
                <button
                  onClick={() => setGrupoAberto(aberto ? null : g.chave)}
                  style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "9px 12px", background: "hsl(var(--background))", border: "none", cursor: "pointer", fontSize: 12.5, color: "hsl(var(--foreground))" }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 999, background: g.cor, flexShrink: 0 }} />
                    <strong>{auditoria[g.chave].total}</strong> {g.label}
                  </span>
                  {aberto ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {aberto && (
                  <div style={{ padding: "8px 12px 12px" }}>
                    <div style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", marginBottom: 8 }}>{g.explicacao}</div>
                    <div style={{ maxHeight: 220, overflowY: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: "left", padding: "4px 8px", color: "hsl(var(--muted-foreground))", fontWeight: 600 }}>Linha</th>
                            <th style={{ textAlign: "left", padding: "4px 8px", color: "hsl(var(--muted-foreground))", fontWeight: 600 }}>Nome</th>
                            <th style={{ textAlign: "left", padding: "4px 8px", color: "hsl(var(--muted-foreground))", fontWeight: 600 }}>Telefone</th>
                            <th style={{ textAlign: "left", padding: "4px 8px", color: "hsl(var(--muted-foreground))", fontWeight: 600 }}>Status na planilha</th>
                          </tr>
                        </thead>
                        <tbody>
                          {linhas.map(l => (
                            <tr key={l.linha} style={{ borderTop: "1px solid hsl(var(--border))" }}>
                              <td style={{ padding: "4px 8px" }}>{l.linha}</td>
                              <td style={{ padding: "4px 8px" }}>{l.nome || "—"}</td>
                              <td style={{ padding: "4px 8px" }}>{l.telefone || "—"}</td>
                              <td style={{ padding: "4px 8px" }}>{l.status_na_planilha || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {auditoria[g.chave].total > linhas.length && (
                      <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginTop: 6 }}>
                        Mostrando {linhas.length} de {auditoria[g.chave].total}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════ Hierarquia importada da planilha ═══════════════════
// Lê a planilha conectada, monta a árvore Gestor→PDV-Equipe→Parceiro
// Indireto→Vendedor por nome, e deixa o admin_company apontar "esse nome é
// essa conta" (vincular) ou criar a conta que falta, direto pela tela.
//
// NUNCA usar os nomes de coluna da planilha (Gestor/PDV-Equipe/Parceiro
// Indireto) em nada visível — só o cargo "CLTista" (Gerente/Coordenador/
// Supervisor/Vendedor). A operação mostra essa tela pra Vivo, que só
// aceita operação CLT; qualquer rastro de "parceiro"/nome de coluna de
// planilha de terceiro tem que sumir do frontend de propósito.
const LABEL_NIVEL_PLANILHA = {
  gerente: "Gerente", coordenador: "Coordenador",
  supervisor: "Supervisor", vendedor: "Vendedor",
};
const ORDEM_NIVEIS = ["gerente", "coordenador", "supervisor", "vendedor"];

function HierarquiaPlanilhaPainel() {
  const [aberto, setAberto] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [criandoNome, setCriandoNome] = useState(null); // nome do nó em criação
  const [formCriar, setFormCriar] = useState({ email: "", password: "", cpf: "" });
  const [salvandoVinculo, setSalvandoVinculo] = useState(null);
  const [reatribuindo, setReatribuindo] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/company/pipeline/sheet-sync/hierarquia-preview");
      setPreview(data);
      setAberto(true);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao carregar hierarquia da planilha — confere se as colunas de Gestor/PDV-Equipe/Parceiro Indireto/Vendedor estão mapeadas."));
    } finally { setLoading(false); }
  };

  const handleVincular = async (no, userId) => {
    if (!userId) return;
    setSalvandoVinculo(no.nome);
    try {
      await api.post("/company/pipeline/sheet-sync/hierarquia-vincular", [{ nome: no.nome, papel: no.papel, user_id: userId }]);
      setPreview(prev => ({
        ...prev,
        niveis: {
          ...prev.niveis,
          [no.papel]: prev.niveis[no.papel].map(n => n.nome === no.nome ? { ...n, existe: true, id: userId, via_vinculo_manual: true } : n),
        },
      }));
      toast.success(`"${no.nome}" vinculado.`);
    } catch (e) { toast.error(getErrorMessage(e, "Erro ao vincular.")); }
    finally { setSalvandoVinculo(null); }
  };

  const handleDesvincular = async (no) => {
    setSalvandoVinculo(no.nome);
    try {
      await api.post("/company/pipeline/sheet-sync/hierarquia-desvincular", { nome: no.nome, papel: no.papel });
      setPreview(prev => ({
        ...prev,
        niveis: {
          ...prev.niveis,
          [no.papel]: prev.niveis[no.papel].map(n => n.nome === no.nome ? { ...n, existe: false, id: null, via_vinculo_manual: false } : n),
        },
      }));
      toast.success(`"${no.nome}" desvinculado.`);
    } catch (e) { toast.error(getErrorMessage(e, "Erro ao desvincular.")); }
    finally { setSalvandoVinculo(null); }
  };

  const handleCriar = async (no) => {
    if (!formCriar.email || formCriar.password.length < 6) { toast.error("Preencha e-mail e senha (mín. 6 caracteres)."); return; }
    try {
      const { data } = await api.post("/company/pipeline/sheet-sync/hierarquia-aplicar", [{
        nome: no.nome, papel: no.papel, reporta_para_nome: no.reporta_para_nome || null,
        email: formCriar.email, password: formCriar.password, cpf: formCriar.cpf || null,
      }]);
      if (data.erros?.length) { toast.error(data.erros[0].erro); return; }
      const criado = data.criados[0];
      setPreview(prev => ({
        ...prev,
        niveis: { ...prev.niveis, [no.papel]: prev.niveis[no.papel].map(n => n.nome === no.nome ? { ...n, existe: true, id: criado.id } : n) },
      }));
      toast.success(`Conta de "${no.nome}" criada!`);
      setCriandoNome(null);
      setFormCriar({ email: "", password: "", cpf: "" });
    } catch (e) { toast.error(getErrorMessage(e, "Erro ao criar conta.")); }
  };

  const handleReatribuir = async () => {
    setReatribuindo(true);
    try {
      const { data } = await api.post("/company/pipeline/sheet-sync/reatribuir-vendedores");
      toast.success(`${data.reatribuidos} card(s) reatribuído(s) pras contas certas.`);
    } catch (e) { toast.error(getErrorMessage(e, "Erro ao reatribuir.")); }
    finally { setReatribuindo(false); }
  };

  return (
    <div style={{ background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 12, padding: 16, marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
            <Users2 size={15} color="#a855f7" /> Hierarquia da planilha
          </div>
          <div style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", marginTop: 2 }}>
            Cruza Gerente / Coordenador / Supervisor / Vendedor com as contas da equipe.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={carregar} disabled={loading} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "1px solid #a855f7", background: "rgba(168,85,247,0.1)", color: "#a855f7", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} {aberto ? "Recarregar" : "Carregar hierarquia"}
          </button>
          {preview && (
            <button onClick={handleReatribuir} disabled={reatribuindo} title="Corrige o dono dos cards já existentes pras contas certas, com base no que estiver vinculado/criado aqui"
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "1px solid hsl(var(--border))", background: "none", color: "hsl(var(--muted-foreground))", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
              {reatribuindo ? <Loader2 size={14} className="animate-spin" /> : <ArrowRightLeft size={14} />} Reatribuir cards antigos
            </button>
          )}
        </div>
      </div>

      {preview && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 14 }}>
          {ORDEM_NIVEIS.filter(papel => (preview.niveis[papel] || []).length > 0).map(papel => (
            <div key={papel}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
                {LABEL_NIVEL_PLANILHA[papel]}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {preview.niveis[papel].map(no => (
                  <div key={no.nome} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 12px", borderRadius: 8, background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", flexWrap: "wrap" }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600 }}>{no.nome}</div>
                    {no.existe ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 11, color: "#22c55e", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>✓ conta vinculada</span>
                        {no.via_vinculo_manual && (
                          <button onClick={() => handleDesvincular(no)} disabled={salvandoVinculo === no.nome}
                            title="Remove o vínculo manual (volta a precisar apontar a conta de novo)"
                            style={{ fontSize: 10.5, padding: "3px 8px", borderRadius: 6, border: "1px solid hsl(var(--border))", background: "none", color: "hsl(var(--muted-foreground))", cursor: "pointer" }}>
                            Desvincular
                          </button>
                        )}
                      </div>
                    ) : criandoNome === no.nome ? (
                      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                        <input placeholder="E-mail" value={formCriar.email} onChange={e => setFormCriar(f => ({ ...f, email: e.target.value }))}
                          style={{ padding: "5px 8px", borderRadius: 6, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", fontSize: 12, width: 160 }} />
                        <input placeholder="Senha" type="password" value={formCriar.password} onChange={e => setFormCriar(f => ({ ...f, password: e.target.value }))}
                          style={{ padding: "5px 8px", borderRadius: 6, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", fontSize: 12, width: 110 }} />
                        <CpfInput small value={formCriar.cpf} onChange={e => setFormCriar(f => ({ ...f, cpf: e.target.value }))} style={{ width: 100 }} />
                        <button onClick={() => handleCriar(no)} style={{ padding: "5px 10px", borderRadius: 6, border: "none", background: "#a855f7", color: "#fff", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>Criar</button>
                        <button onClick={() => setCriandoNome(null)} style={{ padding: "5px 8px", borderRadius: 6, border: "1px solid hsl(var(--border))", background: "none", fontSize: 11.5, cursor: "pointer" }}>Cancelar</button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <select disabled={salvandoVinculo === no.nome} onChange={e => handleVincular(no, e.target.value)} value=""
                          style={{ fontSize: 11.5, padding: "5px 8px", borderRadius: 6, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))" }}>
                          <option value="">Vincular a conta existente...</option>
                          {(preview.contas_por_papel?.[no.papel] || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <button onClick={() => { setCriandoNome(no.nome); setFormCriar({ email: "", password: "", cpf: "" }); }}
                          style={{ fontSize: 11.5, padding: "5px 10px", borderRadius: 6, border: "1px dashed #a855f7", background: "none", color: "#a855f7", fontWeight: 600, cursor: "pointer" }}>
                          + Criar conta
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════ Diagnóstico: cards sem CPF do cliente ═══════════════
// Quebra por etapa do Kanban e por "esteira de agendamento" pra achar o
// padrão de quando o CPF fica sem preencher — é normal (documento só é
// coletado numa etapa mais pra frente) ou é a coluna errada mapeada?
function DiagnosticoCpfPainel() {
  const [aberto, setAberto] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState(null);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/company/pipeline/diagnostico/cpf-faltando");
      setDados(data);
      setAberto(true);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao carregar diagnóstico."));
    } finally { setLoading(false); }
  };

  return (
    <div style={{ background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 12, padding: 16, marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={15} color="#f59e0b" /> Diagnóstico: CPF do cliente faltando
          </div>
          <div style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", marginTop: 2 }}>
            Quebra os cards sem CPF por etapa do Kanban e por esteira de agendamento.
          </div>
        </div>
        <button onClick={carregar} disabled={loading} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "1px solid #f59e0b", background: "rgba(245,158,11,0.1)", color: "#f59e0b", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} {aberto ? "Recarregar" : "Rodar diagnóstico"}
        </button>
      </div>

      {dados && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", gap: 16, marginBottom: 14, flexWrap: "wrap" }}>
            <div><span style={{ fontSize: 20, fontWeight: 800 }}>{dados.total_cards}</span><div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>total de cards</div></div>
            <div><span style={{ fontSize: 20, fontWeight: 800, color: "#f59e0b" }}>{dados.sem_cpf}</span><div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>sem CPF ({dados.percentual_sem_cpf}%)</div></div>
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 240, border: "1px solid hsl(var(--border))", borderRadius: 10, padding: 12, background: "hsl(var(--background))" }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Por etapa do Kanban</div>
              {dados.por_status_kanban.map(r => (
                <div key={r.col} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "3px 0" }}>
                  <span>{COLS.find(c => c.id === r.col)?.label || r.col}</span><span style={{ fontWeight: 700 }}>{r.qtd}</span>
                </div>
              ))}
            </div>
            <div style={{ flex: 1, minWidth: 240, border: "1px solid hsl(var(--border))", borderRadius: 10, padding: 12, background: "hsl(var(--background))" }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Por esteira de agendamento</div>
              {dados.por_esteira_agendamento.map(r => (
                <div key={r.esteira} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "3px 0" }}>
                  <span>{r.esteira}</span><span style={{ fontWeight: 700 }}>{r.qtd}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



function fmtMoeda(v) {
  return (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function ContabilidadeTab({ onEdit }) {
  const [dataDe, setDataDe] = useState("");
  const [dataAte, setDataAte] = useState("");
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState(null);
  const [granularidade, setGranularidade] = useState("dia"); // "dia" | "semana" | "mes"

  const buscar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dataDe) params.set("data_de", dataDe);
      if (dataAte) params.set("data_ate", dataAte);
      const { data } = await api.get(`/company/pipeline/contabilidade?${params.toString()}`);
      setDados(data);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao carregar a Contabilidade."));
    } finally { setLoading(false); }
  }, [dataDe, dataAte]);

  useEffect(() => { buscar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [pagarVenda, setPagarVenda] = useState(null); // venda selecionada pro modal de pagamento
  const [salvandoConfigPlano, setSalvandoConfigPlano] = useState(null); // nome do plano sendo salvo
  const [calculandoAutomatico, setCalculandoAutomatico] = useState(false);

  const handleCalcularAutomatico = useCallback(async () => {
    setCalculandoAutomatico(true);
    try {
      const { data } = await api.post("/company/pipeline/contabilidade/calcular-recebido-automatico");
      if (data.sem_config) {
        toast.error("Configure o % recebido de pelo menos um plano primeiro (tabela abaixo).");
      } else if (data.cards_calculados > 0) {
        toast.success(`${data.cards_calculados} card(s) calculado(s) automaticamente${data.sem_config_de_plano ? ` (${data.sem_config_de_plano} ficaram de fora por não ter % configurado no plano deles)` : ""}.`);
        buscar();
      } else {
        toast.success("Nenhum card pra calcular — ou já está tudo preenchido, ou os planos pendentes ainda não têm % configurado.");
      }
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao calcular automaticamente."));
    } finally { setCalculandoAutomatico(false); }
  }, [buscar]);

  const [abrindoCardId, setAbrindoCardId] = useState(null);
  const abrirCard = useCallback(async (v) => {
    if (!onEdit) return;
    setAbrindoCardId(v.id);
    try {
      // A linha aqui na Contabilidade é uma versão enxuta do card (só os
      // campos financeiros) — busca o card completo pelo número antes de
      // abrir o modal de edição, que espera o doc inteiro (checklist,
      // observações, etc), igual o Kanban e o Histórico já fazem.
      const { data } = await api.get(`/company/pipeline/historico?busca=${encodeURIComponent(v.numero)}`);
      const completo = (data.leads || []).find(l => l.id === v.id);
      if (completo) {
        onEdit(completo);
      } else {
        toast.error("Não achei esse card pra abrir — tenta pelo Histórico de Vendas.");
      }
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao abrir o card."));
    } finally { setAbrindoCardId(null); }
  }, [onEdit]);

  const salvarConfigPlano = useCallback(async (plano, pct) => {
    const valor = parseFloat(pct);
    if (Number.isNaN(valor) || valor < 0) { toast.error("Percentual inválido."); return; }
    setSalvandoConfigPlano(plano);
    try {
      await api.post("/company/pipeline/planos-config", { plano, pct_recebido: valor });
      toast.success(`% recebido de "${plano}" salvo.`);
      buscar();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao salvar o percentual do plano."));
    } finally { setSalvandoConfigPlano(null); }
  }, [buscar]);

  const statCardStyle = { background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 12, padding: "16px 18px" };
  const tableWrapStyle = { background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 12, padding: 16, marginBottom: 16, overflowX: "auto" };
  const thStyle = { textAlign: "left", fontSize: 11, fontWeight: 700, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", padding: "6px 10px", borderBottom: "1px solid hsl(var(--border))" };
  const tdStyle = { fontSize: 12.5, padding: "8px 10px", borderBottom: "1px solid hsl(var(--border))", color: "hsl(var(--foreground))" };

  const resumo = dados?.resumo;
  const dash = dados?.dashboard;
  const miniStatStyle = { background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 12, padding: "14px 16px" };

  return (
    <div>
      {dash && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10, marginBottom: 16 }}>
          <div style={miniStatStyle}>
            <div style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))", marginBottom: 4 }}>Entrou hoje</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "hsl(var(--foreground))" }}>{fmtMoeda(dash.valor_entrado_hoje)}</div>
          </div>
          <div style={miniStatStyle}>
            <div style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))", marginBottom: 4 }}>Finalizadas hoje</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "hsl(var(--foreground))" }}>{dash.vendas_finalizadas_hoje}</div>
          </div>
          <div style={miniStatStyle}>
            <div style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))", marginBottom: 4 }}>Instaladas (total)</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "hsl(var(--foreground))" }}>{dash.vendas_instaladas_total}</div>
          </div>
          <div style={miniStatStyle}>
            <div style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))", marginBottom: 4 }}>Receita hoje (líq.)</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: dash.dia.liquido >= 0 ? "#4ade80" : "#f87171" }}>{fmtMoeda(dash.dia.liquido)}</div>
          </div>
          <div style={miniStatStyle}>
            <div style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))", marginBottom: 4 }}>Receita semana (líq.)</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: dash.semana.liquido >= 0 ? "#4ade80" : "#f87171" }}>{fmtMoeda(dash.semana.liquido)}</div>
          </div>
          <div style={miniStatStyle}>
            <div style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))", marginBottom: 4 }}>Receita mês (líq.)</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: dash.mes.liquido >= 0 ? "#4ade80" : "#f87171" }}>{fmtMoeda(dash.mes.liquido)}</div>
          </div>
        </div>
      )}

      {dash?.vendas_sem_data_instalacao_confiavel > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#f59e0b", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: 8, padding: "8px 12px", marginBottom: 16 }}>
          <AlertTriangle size={13} />
          {dash.vendas_sem_data_instalacao_confiavel} venda(s) instalada(s) sem uma data de instalação confiável (sem "DT Instalação" na planilha nem transição de coluna registrada) — elas entram nos totais e na tabela, mas ficam de fora dos cards de dia/semana/mês e do gráfico abaixo pra não distorcer o período.
        </div>
      )}

      {dash && (dash.serie_diaria?.length > 0 || dash.serie_semanal?.length > 0 || dash.serie_mensal?.length > 0) && (
        <div style={{ background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 14, padding: 18, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "hsl(var(--foreground))" }}>Comparativo — recebido × pago × líquido</div>
            <div style={{ display: "flex", gap: 6 }}>
              {[["dia", "Por dia"], ["semana", "Por semana"], ["mes", "Por mês"]].map(([val, label]) => (
                <button key={val} onClick={() => setGranularidade(val)}
                  style={{
                    padding: "5px 12px", borderRadius: 999, fontSize: 11.5, fontWeight: 700, cursor: "pointer",
                    border: "1px solid " + (granularidade === val ? "#a855f7" : "hsl(var(--border))"),
                    background: granularidade === val ? "#a855f7" : "transparent",
                    color: granularidade === val ? "#fff" : "hsl(var(--muted-foreground))",
                  }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={granularidade === "dia" ? dash.serie_diaria : granularidade === "semana" ? dash.serie_semanal : dash.serie_mensal}
              margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="periodo" tick={{ fontSize: 10.5 }} />
              <YAxis tick={{ fontSize: 10.5 }} tickFormatter={v => fmtMoeda(v)} width={80} />
              <Tooltip formatter={v => fmtMoeda(v)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11.5 }} />
              <Bar dataKey="recebido" name="Recebido operadora" fill="#4ade80" radius={[4, 4, 0, 0]} />
              <Bar dataKey="pago" name="Pago vendedor" fill="#f87171" radius={[4, 4, 0, 0]} />
              <Bar dataKey="liquido" name="Líquido" fill="#a855f7" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={{ background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 14, padding: 18, marginBottom: 20 }}>
        <div style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", marginBottom: 10 }}>
          Mostra só vendas já <strong>Instaladas/Ativas</strong>, filtradas pela data em que viraram instaladas.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 10, marginBottom: 12 }}>
          <label style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>De
            <input type="date" value={dataDe} onChange={e => setDataDe(e.target.value)}
              style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 13 }} />
          </label>
          <label style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>Até
            <input type="date" value={dataAte} onChange={e => setDataAte(e.target.value)}
              style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 13 }} />
          </label>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={buscar} disabled={loading}
            style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#a855f7", color: "#fff", fontSize: 13, fontWeight: 700, cursor: loading ? "default" : "pointer" }}>
            {loading ? "Buscando..." : "Filtrar"}
          </button>
          <button onClick={() => { setDataDe(""); setDataAte(""); }}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid hsl(var(--border))", background: "transparent", color: "hsl(var(--muted-foreground))", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Limpar filtros
          </button>
        </div>
      </div>

      {loading && !dados ? (
        <div style={{ textAlign: "center", padding: 40 }}><Loader2 className="animate-spin" size={22} color="#a855f7" /></div>
      ) : !resumo ? null : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
            <div style={statCardStyle}>
              <div style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}><FileText size={12} /> Vendas no período</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "hsl(var(--foreground))" }}>{resumo.total_vendas}</div>
            </div>
            <div style={statCardStyle}>
              <div style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}><Wallet size={12} /> Pago aos vendedores</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#f87171" }}>{fmtMoeda(resumo.total_pago_vendedor)}</div>
            </div>
            <div style={statCardStyle}>
              <div style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}><TrendingUp size={12} /> Recebido da operadora</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#4ade80" }}>{fmtMoeda(resumo.total_recebido_operadora)}</div>
            </div>
            <div style={statCardStyle}>
              <div style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}><PieChart size={12} /> Margem (recebido − pago)</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: resumo.margem_total >= 0 ? "#4ade80" : "#f87171" }}>{fmtMoeda(resumo.margem_total)}</div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div style={tableWrapStyle}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: 10 }}>Por vendedor</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={thStyle}>Vendedor</th><th style={thStyle}>Vendas</th><th style={thStyle}>Pago</th><th style={thStyle}>Recebido</th></tr></thead>
                <tbody>
                  {dados.por_vendedor.map(v => (
                    <tr key={v.vendedor}>
                      <td style={tdStyle}>{v.vendedor}</td>
                      <td style={tdStyle}>{v.vendas}</td>
                      <td style={{ ...tdStyle, color: "#f87171" }}>{fmtMoeda(v.pago_vendedor)}</td>
                      <td style={{ ...tdStyle, color: "#4ade80" }}>{fmtMoeda(v.recebido_operadora)}</td>
                    </tr>
                  ))}
                  {dados.por_vendedor.length === 0 && <tr><td style={tdStyle} colSpan={4}>Nenhum dado no período.</td></tr>}
                </tbody>
              </table>
            </div>

            <div style={{ ...tableWrapStyle, gridColumn: "1 / -1" }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: 4 }}>Por plano</div>
              <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginBottom: 10 }}>
                Configure o % que a empresa recebe da operadora por plano — ele vira o padrão sugerido no botão "Pagar" de cada venda desse plano.
              </div>
              <button
                onClick={handleCalcularAutomatico}
                disabled={calculandoAutomatico}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 8, border: "1px solid #a855f7", background: "rgba(168,85,247,0.1)", color: "#a855f7", fontSize: 12, fontWeight: 600, cursor: "pointer", marginBottom: 12, opacity: calculandoAutomatico ? 0.6 : 1 }}
              >
                {calculandoAutomatico ? <Loader2 size={13} className="animate-spin" /> : <ArrowRightLeft size={13} />}
                Calcular "Recebido Operadora" automaticamente (venda × % do plano)
              </button>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={thStyle}>Plano</th><th style={thStyle}>Vendas</th>
                  <th style={thStyle}>% recebido (config.)</th>
                  <th style={thStyle}>Pago</th><th style={thStyle}>Recebido</th><th style={thStyle}>Líquido</th>
                </tr></thead>
                <tbody>
                  {dados.por_plano.map(p => (
                    <tr key={p.plano}>
                      <td style={tdStyle}>{p.plano}</td>
                      <td style={tdStyle}>{p.vendas}</td>
                      <td style={tdStyle}>
                        <PlanoPctInput
                          valorInicial={p.pct_recebido_configurado}
                          salvando={salvandoConfigPlano === p.plano}
                          onSalvar={pct => salvarConfigPlano(p.plano, pct)}
                        />
                      </td>
                      <td style={{ ...tdStyle, color: "#f87171" }}>{fmtMoeda(p.pago_vendedor)}</td>
                      <td style={{ ...tdStyle, color: "#4ade80" }}>{fmtMoeda(p.recebido_operadora)}</td>
                      <td style={{ ...tdStyle, color: p.liquido >= 0 ? "#4ade80" : "#f87171", fontWeight: 700 }}>{fmtMoeda(p.liquido)}</td>
                    </tr>
                  ))}
                  {dados.por_plano.length === 0 && <tr><td style={tdStyle} colSpan={6}>Nenhum dado no período.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ ...tableWrapStyle, marginTop: 16 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: 10 }}>Vendas detalhadas</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={thStyle}>Nº</th><th style={thStyle}>Cliente</th><th style={thStyle}>Vendedor</th>
                <th style={thStyle}>Plano</th><th style={thStyle}>Valor venda</th><th style={thStyle}>Pago vendedor</th>
                <th style={thStyle}>Recebido operadora</th><th style={thStyle}>Margem</th><th style={thStyle}>Ponto de atenção</th>
                <th style={thStyle}>Ação</th>
              </tr></thead>
              <tbody>
                {dados.vendas.map(v => (
                  <tr key={v.id} style={v.ponto_atencao ? { background: "rgba(248,113,113,0.06)" } : undefined}>
                    <td style={tdStyle}>
                      <button
                        onClick={() => abrirCard(v)}
                        disabled={abrindoCardId === v.id}
                        title="Abrir esse card pra revisar/editar"
                        style={{ color: "#a855f7", fontWeight: 700, background: "none", border: "none", cursor: abrindoCardId === v.id ? "default" : "pointer", padding: 0, fontSize: "inherit", display: "flex", alignItems: "center", gap: 4, opacity: abrindoCardId === v.id ? 0.6 : 1 }}
                      >
                        {abrindoCardId === v.id && <Loader2 size={11} className="animate-spin" />} #{v.numero}
                      </button>
                    </td>
                    <td style={tdStyle}>{v.name}</td>
                    <td style={tdStyle}>{v.vendedor_nome}</td>
                    <td style={tdStyle}>{v.plano_negociado}</td>
                    <td style={tdStyle}>{fmtMoeda(v.value)}</td>
                    <td style={{ ...tdStyle, color: "#f87171" }}>{fmtMoeda(v.valor_pago_vendedor)}</td>
                    <td style={{ ...tdStyle, color: "#4ade80" }}>{fmtMoeda(v.valor_recebido_operadora)}</td>
                    <td style={{ ...tdStyle, color: v.margem >= 0 ? "#4ade80" : "#f87171", fontWeight: 700 }}>{fmtMoeda(v.margem)}</td>
                    <td style={tdStyle}>
                      {v.ponto_atencao ? (
                        <span title="Falta preencher o valor pago ao vendedor" style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#f87171", fontWeight: 700, fontSize: 11.5 }}>
                          <AlertTriangle size={14} style={{ display: "inline-block", animation: "pontoAtencaoVibra 0.9s ease-in-out infinite" }} />
                          Falta pagar vendedor
                        </span>
                      ) : (
                        <span style={{ color: "#4ade80", fontSize: 11.5 }}>OK</span>
                      )}
                      {!v.data_instalado_confiavel && (
                        <div title="Sem DT Instalação na planilha nem transição de coluna registrada — data abaixo é aproximada" style={{ fontSize: 10, color: "#f59e0b", marginTop: 3 }}>
                          ⚠ data estimada
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <button onClick={() => setPagarVenda(v)}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7, fontSize: 11.5, fontWeight: 700, cursor: "pointer",
                          border: "none", background: v.ponto_atencao ? "#a855f7" : "transparent", color: v.ponto_atencao ? "#fff" : "#a855f7",
                          ...(v.ponto_atencao ? {} : { border: "1px solid #a855f7" }),
                        }}>
                        <Wallet size={13} /> {v.ponto_atencao ? "Pagar" : "Editar"}
                      </button>
                    </td>
                  </tr>
                ))}
                {dados.vendas.length === 0 && <tr><td style={tdStyle} colSpan={10}>Nenhuma venda instalada no período.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {pagarVenda && (
        <PagarVendedorModal
          venda={pagarVenda}
          configsPlanos={dados?.configs_planos || {}}
          onClose={() => setPagarVenda(null)}
          onSalvo={() => { setPagarVenda(null); buscar(); }}
        />
      )}
    </div>

  );
}

function PlanoPctInput({ valorInicial, salvando, onSalvar }) {
  const [valor, setValor] = useState(valorInicial ?? "");
  useEffect(() => { setValor(valorInicial ?? ""); }, [valorInicial]);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <input type="number" min="0" step="0.1" value={valor} placeholder="—"
        onChange={e => setValor(e.target.value)}
        style={{ width: 64, padding: "5px 7px", borderRadius: 6, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", color: "hsl(var(--foreground))", fontSize: 12.5 }} />
      <span style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>%</span>
      <button onClick={() => onSalvar(valor)} disabled={salvando || valor === ""}
        style={{ padding: "4px 9px", borderRadius: 6, border: "1px solid #a855f7", background: "transparent", color: "#a855f7", fontSize: 11, fontWeight: 700, cursor: salvando ? "default" : "pointer" }}>
        {salvando ? "..." : "Salvar"}
      </button>
    </div>
  );
}

function PagarVendedorModal({ venda, configsPlanos, onClose, onSalvo }) {
  const valorVenda = venda.value || 0;
  const pctRecebidoConfig = configsPlanos[venda.plano_negociado];

  const pctPagoInicial = valorVenda > 0 && venda.valor_pago_vendedor
    ? Number(((venda.valor_pago_vendedor / valorVenda) * 100).toFixed(2))
    : 100;

  const [pctPago, setPctPago] = useState(pctPagoInicial);
  const [salvando, setSalvando] = useState(false);

  function round2(n) { return Math.round((n + Number.EPSILON) * 100) / 100; }

  const valorAPagar = round2(valorVenda * (parseFloat(pctPago) || 0) / 100);
  const valorAReceber = pctRecebidoConfig != null
    ? round2(valorVenda * pctRecebidoConfig / 100)
    : (venda.valor_recebido_operadora || 0);

  const aplicar = async () => {
    setSalvando(true);
    try {
      await api.post(`/company/pipeline/${venda.id}/financeiro`, {
        valor_pago_vendedor: valorAPagar,
        valor_recebido_operadora: valorAReceber,
      });
      toast.success(`Pagamento do card #${venda.numero} atualizado.`);
      onSalvo();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao salvar o pagamento."));
    } finally { setSalvando(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 14, padding: 22, width: 380, maxWidth: "90vw" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontSize: 14.5, fontWeight: 800, color: "hsl(var(--foreground))" }}>Card #{venda.numero}</div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "hsl(var(--muted-foreground))" }}><X size={18} /></button>
        </div>
        <div style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))", marginBottom: 18 }}>
          {venda.plano_negociado} · valor da venda <strong style={{ color: "hsl(var(--foreground))" }}>{fmtMoeda(valorVenda)}</strong> (puxado da planilha)
        </div>

        <label style={{ display: "block", fontSize: 11.5, color: "hsl(var(--muted-foreground))", marginBottom: 14 }}>
          Digite o valor a pagar (% da venda)
          <input type="number" min="0" max="1000" step="1" value={pctPago} onChange={e => setPctPago(e.target.value)}
            style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 7, border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 14, fontWeight: 700 }} />
        </label>

        <div style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", marginBottom: 14 }}>
          Valor a receber (operadora): {pctRecebidoConfig != null
            ? <strong style={{ color: "hsl(var(--foreground))" }}>{pctRecebidoConfig}%</strong>
            : <span style={{ color: "#f59e0b" }}>sem % configurado pra esse plano — configure na tabela "Por plano"</span>}
        </div>

        <div style={{ background: "hsl(var(--secondary))", borderRadius: 10, padding: 14, marginBottom: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 6 }}>
            <span style={{ color: "hsl(var(--muted-foreground))" }}>Valor a pagar ao vendedor</span>
            <strong style={{ color: "#f87171" }}>{fmtMoeda(valorAPagar)}</strong>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}>
            <span style={{ color: "hsl(var(--muted-foreground))" }}>Valor a receber da operadora</span>
            <strong style={{ color: "#4ade80" }}>{fmtMoeda(valorAReceber)}</strong>
          </div>
        </div>

        <button onClick={aplicar} disabled={salvando}
          style={{ width: "100%", padding: "10px 0", borderRadius: 9, border: "none", background: "#a855f7", color: "#fff", fontSize: 13.5, fontWeight: 800, cursor: salvando ? "default" : "pointer" }}>
          {salvando ? "Aplicando..." : "Aplicar"}
        </button>
      </div>
    </div>
  );
}

export default function CompanyPipelinePage() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewLead, setShowNewLead] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [view, setView] = useState("kanban"); // "kanban" | "historico" | "boletos" | "sincronizacao"
  const [contabPendente, setContabPendente] = useState(false);

  const PAPEIS_LIDERANCA = ["supervisor", "coordenador", "gerente", "admin_company"];
  const podeGerenciarEquipe = !user?.is_team_member || PAPEIS_LIDERANCA.includes(user?.team_role);
  const isOwner = !user?.is_team_member;
  const podeVerContabilidade = !user?.team_owner_id || user?.team_role === "admin_company";

  useEffect(() => {
    if (!podeVerContabilidade) return;
    let ativo = true;
    const checar = async () => {
      try {
        const { data } = await api.get("/company/pipeline/contabilidade/pendencias");
        if (ativo) setContabPendente(!!data?.tem_pendencia);
      } catch { /* silencioso — só é o sininho do menu */ }
    };
    checar();
    const id = setInterval(checar, 60000);
    return () => { ativo = false; clearInterval(id); };
  }, [podeVerContabilidade]);

  // Rastreia a última movimentação já "vista" de cada card, pra saber quando
  // avisar que ALGUÉM (não eu) acabou de mexer nele. firstLoadRef evita
  // disparar notificação de tudo que já existia antes de eu abrir a página.
  const seenRef = useRef(new Map());
  const firstLoadRef = useRef(true);

  const checkForNotifications = useCallback((newLeads) => {
    for (const lead of newLeads) {
      const hist = lead.historico || [];
      const last = hist[hist.length - 1];
      if (!last) continue;
      const prevSeen = seenRef.current.get(lead.id);
      if (prevSeen !== last.data) {
        if (!firstLoadRef.current && prevSeen !== undefined && last.usuario_id !== user?.id) {
          const quem = last.usuario_nome || "Alguém";
          const alvo = `${lead.numero ? `#${lead.numero} ` : ""}"${lead.name}"`;
          const msg = last.tipo === "observacao"
            ? `${quem} editou as observações de ${alvo}`
            : `${quem} moveu ${alvo} para "${colLabel(last.para)}"`;
          toast(msg, { icon: "🔔", duration: 6000 });
        }
        seenRef.current.set(lead.id, last.data);
      }
    }
    firstLoadRef.current = false;
  }, [user?.id]);

  const fetchLeads = useCallback(async (opts = {}) => {
    if (!firstLoadRef.current) setRefreshing(true);
    try {
      const { data } = await api.get("/company/pipeline");
      setLeads(data.leads);
      checkForNotifications(data.leads);
    } catch (e) {
      if (!opts.silent) toast.error(getErrorMessage(e, "Erro ao carregar o pipeline."));
    } finally { setLoading(false); setRefreshing(false); }
  }, [checkForNotifications]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  // Poll a cada 20s pra pegar movimentações de outros usuários da equipe
  // sem precisar dar F5. É "quase tempo real" sem precisar de websocket.
  useEffect(() => {
    const iv = setInterval(() => fetchLeads({ silent: true }), 20000);
    return () => clearInterval(iv);
  }, [fetchLeads]);

  const [pendingMove, setPendingMove] = useState(null); // { id, col } | null
  const historicoRef = useRef(null);

  const performMove = async (id, col, nota = "", data_instalacao = "") => {
    setLeads(prev => prev.map(l => l.id === id ? { ...l, col, ...(nota && { nota_bko: nota }), ...(data_instalacao && { data_instalacao }) } : l));
    try {
      await api.post(`/company/pipeline/${id}/move`, { col, nota, data_instalacao });
      fetchLeads({ silent: true }); // pega o histórico atualizado (com a entrada nova) sem re-notificar
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao mover o card."));
      fetchLeads();
    }
  };

  const handleMove = (id, col) => {
    if (COLS_COM_NOTA.has(col) || COLS_COM_DATA.has(col)) {
      setPendingMove({ id, col });
    } else {
      performMove(id, col);
    }
  };

  const handleDelete = async (id) => {
    setLeads(prev => prev.filter(l => l.id !== id));
    try {
      await api.delete(`/company/pipeline/${id}`);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao remover."));
      fetchLeads();
    }
  };

  const handleDeleteColuna = async (col) => {
    const rotulo = colLabel(col);
    const qtd = leads.filter(l => l.col === col).length;
    if (!window.confirm(`Apagar TODOS os ${qtd} card(s) da coluna "${rotulo}"? Essa ação não pode ser desfeita.`)) return;
    setLeads(prev => prev.filter(l => l.col !== col));
    try {
      const { data } = await api.delete(`/company/pipeline/coluna/${col}`);
      toast.success(`${data.removidos} card(s) removido(s) da coluna "${rotulo}".`);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao apagar os cards da coluna."));
      fetchLeads();
    }
  };

  const handleDrop = (col) => {
    if (draggingId) {
      if (col === "concluido" && user?.team_role === "vendedor") {
        toast.error("Só backoffice/coordenador pode marcar um card como concluído.");
        setDraggingId(null);
        return;
      }
      handleMove(draggingId, col);
    }
    setDraggingId(null);
  };

  if (loading) {
    return (
      <div style={{ padding: 40, display: "flex", justifyContent: "center" }}>
        <Loader2 className="animate-spin" size={22} color="hsl(var(--muted-foreground))" />
      </div>
    );
  }

  return (
    <div style={{ padding: "28px 20px 60px" }}>
      <style>{`
        @keyframes contabPulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.35); opacity: 0.55; } }
        @keyframes pontoAtencaoVibra {
          0%, 100% { transform: rotate(0deg); }
          20% { transform: rotate(-12deg); }
          40% { transform: rotate(10deg); }
          60% { transform: rotate(-8deg); }
          80% { transform: rotate(6deg); }
        }
      `}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <Users2 size={22} color="#a855f7" />
        <h1 style={{ fontSize: 22, fontWeight: 800, color: "hsl(var(--foreground))" }}>Pipeline de Empresa</h1>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: "#a855f7", background: "rgba(168,85,247,0.12)", padding: "2px 8px", borderRadius: 999 }}>BETA</span>
      </div>
      <p style={{ fontSize: 13.5, color: "hsl(var(--muted-foreground))", marginBottom: 20 }}>
        Pipeline com etapas de backoffice (Enviado BKO → Para Correção / Cancelado / Concluído / Instalado / Ativo).
        Vendedor só vê e mexe nos próprios leads; backoffice/coordenador vê e mexe no pipeline inteiro
        da equipe. Só backoffice/coordenador pode marcar um card como Concluído.
      </p>

      {(() => {
        const abaBtn = (ativa) => ({
          padding: "9px 16px", fontSize: 13, fontWeight: 700, borderRadius: 8,
          border: ativa ? "1px solid rgba(168,85,247,0.35)" : "1px solid transparent",
          background: ativa ? "rgba(168,85,247,0.14)" : "transparent",
          color: ativa ? "#a855f7" : "hsl(var(--muted-foreground))",
          cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
          transition: "background .15s, color .15s, border-color .15s",
        });
        return (
          <div style={{ display: "flex", gap: 4, marginBottom: 22, paddingBottom: 14, borderBottom: "1px solid hsl(var(--border))", flexWrap: "wrap" }}>
            <button onClick={() => setView("kanban")} style={abaBtn(view === "kanban")}>
              <Users2 size={14} /> Kanban
            </button>
            <button onClick={() => setView("historico")} style={abaBtn(view === "historico")}>
              <History size={14} /> Histórico de Vendas
            </button>
            <button onClick={() => setView("boletos")} style={abaBtn(view === "boletos")}>
              <FileText size={14} /> Boletos
            </button>
            <button onClick={() => setView("sincronizacao")} style={abaBtn(view === "sincronizacao")}>
              <Table2 size={14} /> Sincronização de Base
            </button>
            <button onClick={() => setView("indicadores")} style={abaBtn(view === "indicadores")}>
              <BarChart3 size={14} /> Indicadores
            </button>
            {podeVerContabilidade && (
              <button onClick={() => { setView("contabilidade"); setContabPendente(false); }} style={{ ...abaBtn(view === "contabilidade"), position: "relative" }}>
                <PieChart size={14} /> Contabilidade
                {contabPendente && (
                  <span style={{
                    position: "absolute", top: -4, right: -4, width: 10, height: 10, borderRadius: "50%",
                    background: "#f87171", border: "2px solid hsl(var(--background))",
                    animation: "contabPulse 1.4s ease-in-out infinite",
                  }} />
                )}
              </button>
            )}
          </div>
        );
      })()}

      {view === "boletos" ? (
        <BoletosTab />
      ) : view === "sincronizacao" ? (
        <SincronizacaoBaseTab />
      ) : view === "indicadores" ? (
        <IndicadoresTab />
      ) : view === "contabilidade" ? (
        <ContabilidadeTab onEdit={setEditingLead} />
      ) : view === "historico" ? (
        <HistoricoVendas ref={historicoRef} onEdit={setEditingLead} />
      ) : (
      <>
      <TeamSection isOwner={podeGerenciarEquipe} meuTeamRole={user?.team_role} meuId={user?.id} />
      <ParceirosSection podeGerenciar={user?.team_role !== "vendedor"} />

      <div style={{ marginBottom: 14, display: "flex", gap: 8, alignItems: "center" }}>
        {showNewLead ? (
          <NewLeadForm onCreated={(l) => setLeads(prev => [l, ...prev])} onClose={() => setShowNewLead(false)} />
        ) : (
          <>
            <button onClick={() => setShowNewLead(true)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "1px solid #a855f7", background: "rgba(168,85,247,0.1)", color: "#a855f7", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              <Plus size={15} /> Novo lead
            </button>
            <button onClick={() => fetchLeads()} disabled={refreshing}
              title="Atualizar agora"
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "1px solid hsl(var(--border))", background: "transparent", color: "hsl(var(--muted-foreground))", fontSize: 13, fontWeight: 600, cursor: refreshing ? "default" : "pointer" }}>
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Atualizar
            </button>
          </>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12, alignItems: "start" }}>
        {COLS.map(col => {
          const colLeads = leads.filter(l => l.col === col.id);
          return (
            <div
              key={col.id}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(col.id)}
              style={{ background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 12, padding: 10, display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 260px)" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, paddingBottom: 8, borderBottom: `2px solid ${col.color}`, flexShrink: 0 }}>
                <span style={{ width: 8, height: 8, borderRadius: 999, background: col.color, flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: "hsl(var(--foreground))" }}>{col.label}</span>
                <span style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))", marginLeft: "auto" }}>{colLeads.length}</span>
              </div>
              <div
                className="scroll-thin"
                style={{ overflowY: "auto", overflowX: "hidden", flex: 1, minHeight: 60, paddingRight: 2 }}
                onDragOver={(e) => {
                  e.preventDefault();
                  // Auto-scroll: se o mouse chegar perto do topo/fundo da
                  // área com cards enquanto arrasta, rola sozinho — sem
                  // isso, arrastar um card lá de baixo pra uma coluna
                  // grande obrigava a soltar o drag, rolar na mão, e
                  // arrastar de novo.
                  const el = e.currentTarget;
                  const rect = el.getBoundingClientRect();
                  const margem = 56;
                  const distTopo = e.clientY - rect.top;
                  const distFundo = rect.bottom - e.clientY;
                  if (distTopo < margem) {
                    el.scrollTop -= Math.ceil((margem - distTopo) / 2.5);
                  } else if (distFundo < margem) {
                    el.scrollTop += Math.ceil((margem - distFundo) / 2.5);
                  }
                }}
                onDrop={(e) => { e.stopPropagation(); handleDrop(col.id); }}
              >
                {colLeads.map(lead => (
                  <Card key={lead.id} lead={lead} onMove={handleMove} onDelete={handleDelete} onDeleteColuna={handleDeleteColuna} onDragStart={setDraggingId} onEdit={setEditingLead} teamRole={user?.team_role} isAdminCompany={!user?.team_owner_id || user?.team_role === "admin_company"} />
                ))}
                {colLeads.length === 0 && (
                  <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", textAlign: "center", padding: "16px 0" }}>Vazio</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      </>
      )}

      {editingLead && (
        <EditLeadModal
          lead={editingLead}
          teamRole={user?.team_role}
          isAdminCompany={!user?.team_owner_id || user?.team_role === "admin_company"}
          onClose={() => setEditingLead(null)}
          onSaved={(updated) => {
            setLeads(prev => prev.map(l => l.id === updated.id ? { ...l, ...updated } : l));
            historicoRef.current?.reload();
          }}
        />
      )}

      {pendingMove && (
        <MoveNoteModal
          col={pendingMove.col}
          needsData={COLS_COM_DATA.has(pendingMove.col)}
          onCancel={() => setPendingMove(null)}
          onConfirm={({ nota, data_instalacao }) => {
            performMove(pendingMove.id, pendingMove.col, nota, data_instalacao);
            setPendingMove(null);
          }}
        />
      )}
    </div>
  );
}
